import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  AZURE_REALTIME_PROVIDER,
  effortOptionsFor,
  formToRealtime,
  GEMINI_PROVIDER,
  PREFIX_PADDING_RANGE,
  providerSettings,
  REALTIME_SPEED_RANGE,
  type RealtimeAudioForm,
  realtimeToForm,
  reasoningEffortsOf,
  SILENCE_DURATION_RANGE,
} from "@/lib/agentModel";
import {
  fromGrid,
  type GridCell,
  hasGridErrors,
  toGrid,
  WeeklyBlocksEditor,
} from "@/components/WeeklyBlocksEditor";
import { api } from "@/lib/api";
import {
  type AgentProvider,
  type EmmyTicketType,
  type FiledCalls,
  type OutOfHoursLanguages,
  type OpeningHoursBlock,
  type TransferWindow,
  type PracticeAgentPatch,
  type PracticeAgentSettings,
  type PracticeFull,
  type PracticeLanguage,
  type Prompt,
  type RequestType,
  type SpeechDetection,
  type TransferConfirmMode,
  type VacationBlock,
} from "@/lib/types";
import {
  allNumbersSame,
  parseKeywordLines,
  realtimeTransferRequestTypeErrors,
  realtimeTransfersUrgentCalls,
  routeNumbersPatch,
  transferRequestTypeErrors,
} from "@/lib/transfer";

const SPEED_MIN = 0.7;
const SPEED_MAX = 1.2;
// V014 — scripted (V1) STT endpointing tuning bounds. Must match the API clamps in ApiHandler.validateSttTuning.
const SPEECH_END_MIN = 0.5;
const SPEECH_END_MAX = 3.0;
const SPEECH_START_MIN = 2;
const SPEECH_START_MAX = 30;
const ENDPOINTING_SENSITIVITY_OPTIONS: { value: string; label: string }[] = [
  {
    value: "ENDPOINTING_SENSITIVITY_STANDARD",
    label: "Standardní (nejtrpělivější)",
  },
  {
    value: "ENDPOINTING_SENSITIVITY_SHORT",
    label: "Kratší (rychlejší reakce)",
  },
  {
    value: "ENDPOINTING_SENSITIVITY_SUPERSHORT",
    label: "Nejkratší (nejrychlejší reakce)",
  },
];
/** The catch-all request type — Classify falls back to this when no other type matches. Always present on every
 *  practice; cannot be deleted or renamed (FK cascade risk). Admin can still edit its displayName + criteria + Emmy
 *  mapping. */
const JINE_TYPE_ID = "jine";

/** Reads the cached `/api/me` response. The `["me"]` query is populated once at app mount (see `AuthenticatedApp`),
 *  so this is synchronous-feeling — no additional network traffic per section. DoctorUser sees a pared-down set of
 *  fields across the practice settings (PRD-010 Stage 2 §Phase 2.2 "subset of admin's PracticeSettings").
 */
const useIsDoctor = (): boolean => {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, staleTime: 60_000 });
  return me.data?.kind === "doctor";
};

/** Layout for everything under `/practices/:id/...`. Fetches the practice once, shows the practice title + a tab
 *  bar, and renders the active tab via `<Outlet />`. The five tabs (Ordinace, Hlášky, Typy požadavků, Ordinační
 *  hodiny, Dovolená) are the sub-pages the practice settings were split into — visible to both admin and doctor
 *  users (PRD-010 Stage 2). DangerZone (hard-delete) was removed per the same session's design call.
 */
export const PracticeDetailLayout = () => {
  const { id = "" } = useParams();
  const isDoctor = useIsDoctor();
  const location = useLocation();
  const query = useQuery({
    queryKey: ["practice", id],
    queryFn: () => api.getPractice(id),
    enabled: id.length > 0,
  });

  // Souhrn puts a chat panel beside the content, so it gets the wider container. Every other tab is a column of forms
  // that reads worse the wider it runs.
  const width = location.pathname.endsWith("/summary")
    ? "max-w-7xl"
    : "max-w-5xl";

  // Doctor nav has its tab links at the top level (in `DoctorApp`), so the layout strips its own header and the
  // dedicated tab bar — just fetch + outlet. Admin keeps the two-tier layout: practice title + tab bar + content.
  if (isDoctor) {
    return (
      <div className={`mx-auto ${width} p-8`}>
        {query.isPending && <p className="text-gray-500">Načítám…</p>}
        {query.isError && (
          <p className="text-red-600">
            Chyba: {(query.error as Error).message}
          </p>
        )}
        {query.isSuccess && (
          <Outlet context={query.data satisfies PracticeFull} />
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="bg-[#1a2b6b] shadow-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 pt-4">
          <div className="flex min-w-0 flex-col gap-1">
            <Link
              to="/practices"
              className="text-xs text-white/70 hover:text-white"
            >
              ← Ordinace
            </Link>
            <h2 className="truncate text-xl tracking-tight text-white">
              {query.data?.displayName ?? id}
            </h2>
          </div>
        </div>
        <nav className="mx-auto max-w-5xl px-6 pt-3">
          <ul className="flex flex-wrap gap-0 text-sm">
            <PracticeTab to="summary">Souhrn</PracticeTab>
            <PracticeTab to="facility">Ordinace</PracticeTab>
            <PracticeTab to="messages">Hlášky</PracticeTab>
            <PracticeTab to="request-types">Typy požadavků</PracticeTab>
            <PracticeTab to="hours">Ordinační hodiny</PracticeTab>
            <PracticeTab to="out-of-office">Dovolená</PracticeTab>
            {/* Shown once a practice has V2 settings, which outlast a switch back to V1 — so a practice being moved
                between generations keeps the tab rather than having it flicker away with its contents still stored. */}
            {(query.data?.agentMode === "realtime" || query.data?.agent) && (
              <PracticeTab to="agent">Agent</PracticeTab>
            )}
          </ul>
        </nav>
      </div>
      <div className={`mx-auto ${width} p-8`}>
        {query.isPending && <p className="text-gray-500">Načítám…</p>}
        {query.isError && (
          <p className="text-red-600">
            Chyba: {(query.error as Error).message}
          </p>
        )}
        {query.isSuccess && (
          <Outlet context={query.data satisfies PracticeFull} />
        )}
      </div>
    </div>
  );
};

const PracticeTab = ({ to, children }: { to: string; children: ReactNode }) => (
  <li>
    <NavLink
      to={to}
      className={({ isActive }) =>
        "inline-block border-b-2 px-4 py-2.5 transition-colors " +
        (isActive
          ? "border-white font-medium text-white"
          : "border-transparent text-white/70 hover:text-white")
      }
    >
      {children}
    </NavLink>
  </li>
);

/** Each sub-page pulls the fetched practice off the outlet context — avoids refetching per tab and keeps the
 *  section components' props-based shape unchanged.
 */
const usePractice = (): PracticeFull => useOutletContext<PracticeFull>();

export const PracticeFacilityPage = () => {
  const practice = usePractice();
  return (
    <div className="space-y-6">
      <EmmyAttachmentsWarning practice={practice} />
      <GeneralSection practice={practice} />
      <EmmyConnectionSection practice={practice} />
      <DangerZoneSection practice={practice} />
    </div>
  );
};

/** Warns when Emmy will not accept the call recording, and offers to fix it.
 *
 *  Emmy's `CreateTicketAttachment` requires `SecureAttachmentsModule`, and when it is off it rejects the request with a
 *  400 whose body carries no message. Nothing about that says which module, so the only visible symptom is that every
 *  call's recording is missing. This turns a silent data-loss setting into something an admin can see and switch on.
 *
 *  Only rendered once the check has actually come back negative: a practice with no Emmy credentials yet cannot be
 *  queried, and a spinner or an error on this page would be noise on the far more common happy path.
 */
const EmmyAttachmentsWarning = ({ practice }: { practice: PracticeFull }) => {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ["emmy-attachments", practice.id],
    queryFn: () => api.getEmmyAttachmentsStatus(practice.id),
    retry: false,
  });

  const enable = useMutation({
    mutationFn: () => api.enableEmmyAttachments(practice.id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["emmy-attachments", practice.id] }),
  });

  // Nothing is rendered only when the answer is a definite yes. Every other state says something.
  //
  // The first version of this hid itself whenever the check had not succeeded — while loading, on error, and when it
  // was gated behind stored credentials. That reproduced the exact bug it exists to expose: a setting silently wrong
  // and a page showing nothing. An unanswerable check is itself worth reporting, because the consequence of the
  // setting being off is that every call loses its recording.
  if (status.isSuccess && status.data.enabled) return null;

  if (status.isPending) {
    return (
      <p className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
        Ověřuji, zda Emmy u této ordinace přijímá přílohy…
      </p>
    );
  }

  if (status.isError) {
    return (
      <div className="rounded border border-gray-300 bg-gray-50 p-4">
        <h3 className="text-sm font-semibold text-gray-800">
          Nepodařilo se ověřit nastavení příloh v Emmy
        </h3>
        <p className="mt-1 text-xs text-gray-600">
          Pokud jsou přílohy vypnuté, Emmy odmítne nahrávku každého hovoru.
          Zkontrolujte propojení s Emmy níže. Chyba:{" "}
          {(status.error as Error).message}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-4">
      <h3 className="text-sm font-semibold text-amber-900">
        Emmy nepřijímá přílohy — nahrávky hovorů se neukládají
      </h3>
      <p className="mt-1 text-xs text-amber-800">
        Tato ordinace má v Emmy vypnuté přílohy (modul{" "}
        <code className="text-[11px]">SecureAttachmentsModule</code>). Emmy
        proto odmítne nahrávku každého hovoru a v požadavku zůstane jen souhrn a
        přepis. Požadavek se založí, ale zvukový záznam v něm nebude.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          onClick={() => enable.mutate()}
          disabled={enable.isPending}
          className="rounded bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {enable.isPending ? "Zapínám…" : "Zapnout přílohy v Emmy"}
        </button>
        {enable.isError && (
          <span className="text-xs text-red-700">
            {(enable.error as Error).message}
          </span>
        )}
      </div>
    </div>
  );
};

export const PracticeMessagesPage = () => {
  const practice = usePractice();
  return (
    <div className="space-y-6">
      <VoiceSettingsPanel practice={practice} />
      {practice.agentMode === "scripted" && (
        <SttTimingPanel practice={practice} />
      )}
      {practice.agentMode === "scripted" && (
        <IdentificationSection practice={practice} />
      )}
      <LanguagesSection practice={practice} />
      {practice.agentMode === "scripted" && (
        <InfoMessageSection practice={practice} />
      )}
      <PromptsSection
        practiceId={practice.id}
        initial={practice.prompts}
        agentMode={practice.agentMode}
        languages={practice.languages ?? []}
      />
    </div>
  );
};

export const PracticeRequestTypesPage = () => {
  const practice = usePractice();
  return (
    <div className="space-y-6">
      <FiledCallsSection practice={practice} />
      <RequestTypesSection practice={practice} />
    </div>
  );
};

const FILED_CALLS_OPTIONS: {
  value: FiledCalls;
  label: string;
  hint: string;
}[] = [
  {
    value: "requests",
    label: "Jen hovory s požadavkem",
    hint: "Hovory, kde volající nic neřekl nebo si jen poslechl informace, se nezapíší.",
  },
  {
    value: "requests_and_silent",
    label: "Hovory s požadavkem a tiché hovory",
    hint: "Navíc se zapíše hovor, kde volající nepromluvil, s výzvou ke zpětnému volání.",
  },
  {
    value: "all",
    label: "Všechny hovory",
    hint: "Zapíše se každý hovor, i když si volající jen poslechl informace nebo byl přepojen.",
  },
];

const FiledCallsSection = ({ practice }: { practice: PracticeFull }) => {
  const qc = useQueryClient();
  const initial = practice.filedCalls ?? "requests_and_silent";
  const [filedCalls, setFiledCalls] = useState<FiledCalls>(initial);

  const mutation = useMutation({
    mutationFn: async () => {
      await api.updatePractice(practice.id, {
        displayName: practice.displayName,
        enabled: practice.enabled,
        voiceId: practice.voiceId,
        voiceSpeed: practice.voiceSpeed,
        filedCalls,
      });
      await api.renderPrompts(practice.id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["practices"] });
      pollPracticeUntilRenderedOrTimeout(qc, practice.id);
    },
  });

  return (
    <Section
      title={
        practice.agentMode === "realtime"
          ? "Které hovory zapsat do Emmy"
          : "Ke kterým hovorům založit požadavek"
      }
      actions={
        <SaveButton mutation={mutation} dirty={filedCalls !== initial} />
      }
    >
      <div className="space-y-3">
        {FILED_CALLS_OPTIONS.map((option) => (
          <label key={option.value} className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="filedCalls"
              checked={filedCalls === option.value}
              onChange={() => setFiledCalls(option.value)}
              className="mt-0.5"
            />
            <span>
              {option.label}
              <span className="block text-xs text-gray-500">{option.hint}</span>
            </span>
          </label>
        ))}
      </div>
      <p className="mt-4 text-xs text-gray-500">
        Urgentní a firemní hovory, nezdařené přepojení a hovory, které skončily
        předčasně, když už volající mluvil, se zapíší vždy.
      </p>
    </Section>
  );
};

export const PracticeHoursPage = () => {
  const practice = usePractice();
  return (
    <>
      <OpeningHoursSection
        practiceId={practice.id}
        initial={practice.openingHours}
      />
      <OutOfHoursSection practice={practice} />
      {/* Scheduled windows apply to both generations: a window means a human answers now, whichever agent would
          otherwise have picked up. The press-9 controls are V1 only — V2 decides transfers through its own tool and a
          second blind route would race it. */}
      <TransferSettingsSection practice={practice} />
      <TransferWindowsSection
        practiceId={practice.id}
        initial={practice.transferWindows ?? []}
      />
    </>
  );
};

const OUT_OF_HOURS_LANGUAGE_OPTIONS: {
  value: OutOfHoursLanguages;
  label: string;
  hint: string;
  scriptedOnly: boolean;
}[] = [
  {
    value: "main",
    label: "Jen v hlavním jazyce",
    hint: "Volající uslyší hlášku Mimo ordinační hodiny jen v hlavním jazyce ordinace.",
    scriptedOnly: false,
  },
  {
    value: "in_turn",
    label: "Postupně ve všech jazycích",
    hint: "Hláška zazní nejdřív v hlavním jazyce a pak postupně v ostatních zapnutých jazycích.",
    scriptedOnly: false,
  },
  {
    value: "keypad",
    label: "Výběr jazyka klávesou",
    hint: "Po hlášce v hlavním jazyce zazní nabídka jazyků. Volající stiskne klávesu a uslyší hlášku ve zvoleném jazyce.",
    scriptedOnly: true,
  },
];

const OutOfHoursSection = ({ practice }: { practice: PracticeFull }) => {
  const qc = useQueryClient();
  const isScripted = practice.agentMode === "scripted";
  const hasExtraLanguages = (practice.languages ?? []).some(
    (l) => !l.isMain && l.enabled,
  );
  const canOfferInfo = isScripted && (practice.infoMessageEnabled ?? false);

  const initialLanguages = practice.outOfHoursLanguages ?? "main";
  const initialInfo = practice.infoMessageOutOfHours ?? false;
  const [languages, setLanguages] =
    useState<OutOfHoursLanguages>(initialLanguages);
  const [info, setInfo] = useState(initialInfo);

  const mutation = useMutation({
    mutationFn: async () => {
      await api.updatePractice(practice.id, {
        displayName: practice.displayName,
        enabled: practice.enabled,
        voiceId: practice.voiceId,
        voiceSpeed: practice.voiceSpeed,
        outOfHoursLanguages: languages,
        infoMessageOutOfHours: info,
      });
      await api.renderPrompts(practice.id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["practices"] });
      pollPracticeUntilRenderedOrTimeout(qc, practice.id);
    },
  });

  if (!hasExtraLanguages && !canOfferInfo) {
    return null;
  }

  const options = OUT_OF_HOURS_LANGUAGE_OPTIONS.filter(
    (o) => isScripted || !o.scriptedOnly,
  );

  return (
    <Section
      title="Mimo ordinační hodiny"
      actions={
        <SaveButton
          mutation={mutation}
          dirty={languages !== initialLanguages || info !== initialInfo}
        />
      }
    >
      <div className="space-y-4">
        {hasExtraLanguages && (
          <div className="space-y-3">
            {options.map((option) => (
              <label
                key={option.value}
                className="flex items-start gap-2 text-sm"
              >
                <input
                  type="radio"
                  name="outOfHoursLanguages"
                  checked={languages === option.value}
                  onChange={() => setLanguages(option.value)}
                  className="mt-0.5"
                />
                <span>
                  {option.label}
                  <span className="block text-xs text-gray-500">
                    {option.hint}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}

        {canOfferInfo && (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={info}
              onChange={(e) => setInfo(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Nabízet nahranou informaci i mimo ordinační hodiny
              <span className="block text-xs text-gray-500">
                Po hlášce zazní nabídka nahrané informace a volající má 5 sekund
                na stisk osmičky. Během dovolené se nenabízí.
              </span>
            </span>
          </label>
        )}
      </div>
    </Section>
  );
};

export const PracticeOutOfOfficePage = () => {
  const practice = usePractice();
  return (
    <VacationBlocksSection
      practiceId={practice.id}
      initial={practice.vacationBlocks}
      credsConfigured={!!practice.emmyCredentialsStoredAt}
    />
  );
};

// --- General ---

const DidRoutingStatus = ({
  savedDid,
  lastSynced,
  edited,
}: {
  savedDid: string;
  lastSynced: string | null;
  edited: boolean;
}) => {
  if (savedDid === "" || edited) {
    return null;
  }

  if (lastSynced === null) {
    return (
      <p className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
        Číslo zatím není v routovací mapě, kterou čte <code>agent</code>. Hovory
        na něj budou vyzvánět a nikdo je nepřijme, dokud se mapa neobnoví.
      </p>
    );
  }

  return (
    <p className="mt-1 text-[11px] text-gray-400">
      Nasazeno do LiveKit + S3: {new Date(lastSynced).toLocaleString("cs-CZ")}
    </p>
  );
};

const GeneralSection = ({ practice }: { practice: PracticeFull }) => {
  const qc = useQueryClient();
  const isDoctor = useIsDoctor();
  const [displayName, setDisplayName] = useState(practice.displayName);
  const [enabled, setEnabled] = useState(practice.enabled);
  // PRD-011: phone_did is admin-only. `PracticeFull` extends `Practice` so both fields are on the inherited type.
  // BE now encodes them explicitly on the PracticeFull JSON (previously the Scala case class dropped them, so the
  // input rendered blank even when a DID was stored on the practice row).
  const initialPhoneDid = practice.phoneDid ?? "";
  const initialLastSynced = practice.phoneDidLastSyncedAt ?? null;
  const [phoneDid, setPhoneDid] = useState(initialPhoneDid);
  // V017: the carrier delivering this DID. Decides how a blind transfer is performed,
  // so it belongs next to the number rather than in the agent settings.
  const initialSipCarrier = practice.sipCarrier ?? "odorik";
  const initialSipReferHost = practice.sipReferHost ?? "";
  const [sipCarrier, setSipCarrier] = useState(initialSipCarrier);
  const [sipReferHost, setSipReferHost] = useState(initialSipReferHost);
  // V013: which engine answers this practice's calls. Switching keeps both
  // generations' settings — they live in different places, so neither can
  // overwrite the other.
  const initialAgentMode = practice.agentMode ?? "scripted";
  const [agentMode, setAgentMode] = useState(initialAgentMode);

  const mutation = useMutation({
    mutationFn: async () => {
      // E.164 normalise: strip spaces. Empty string means "clear the DID" (sent as null).
      const normalised = phoneDid.trim();
      await api.updatePractice(practice.id, {
        displayName,
        enabled,
        voiceId: practice.voiceId,
        voiceSpeed: practice.voiceSpeed,
        ...(isDoctor
          ? {}
          : {
              phoneDid: normalised.length > 0 ? normalised : null,
              sipCarrier,
              // Only IPEX consults this; sending it for the others would store a value
              // that silently does nothing.
              sipReferHost:
                sipCarrier === "ipex" && sipReferHost.trim().length > 0
                  ? sipReferHost.trim()
                  : null,
              agentMode,
            }),
      });
      // The agent reads agentMode, the DID and the carrier from the manifest in
      // S3, not from the database, and nothing republishes it on an update. So
      // without this the V1/V2 switch flips the row, reports success, and every
      // call carries on running the old generation — worst of all when the
      // switch is a rollback away from a V2 that is misbehaving.
      await api.renderPrompts(practice.id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["practices"] });
      void qc.invalidateQueries({ queryKey: ["practice", practice.id] });
      pollPracticeUntilRenderedOrTimeout(qc, practice.id);
    },
  });

  const dirty =
    displayName !== practice.displayName ||
    enabled !== practice.enabled ||
    (!isDoctor &&
      (phoneDid.trim() !== (initialPhoneDid ?? "") ||
        sipCarrier !== initialSipCarrier ||
        sipReferHost.trim() !== (initialSipReferHost ?? "") ||
        agentMode !== initialAgentMode));

  return (
    <Section
      title="Obecné"
      actions={<SaveButton mutation={mutation} dirty={dirty} />}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Název">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
          />
        </Field>
        <Field label="Aktivní">
          <label className="mt-1 inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Služba přijímá hovory</span>
          </label>
        </Field>
        {!isDoctor && (
          <Field label="Telefonní číslo (DID)">
            <input
              type="tel"
              value={phoneDid}
              onChange={(e) => setPhoneDid(e.target.value)}
              placeholder="e.g. +420315315905"
              // Grey background when a DID is already saved (visual cue that this is normally-unchanged config);
              // fully editable either way — Tim asked to discourage changes, not block them.
              className={`w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm placeholder:text-gray-300 focus:border-[#1a2b6b] focus:outline-none ${
                (practice.phoneDid ?? "").length > 0
                  ? "bg-gray-100"
                  : "bg-white"
              }`}
              autoComplete="off"
              spellCheck={false}
            />
            <p className="mt-1 text-[11px] text-gray-500">
              Kanonický E.164 formát. Uložení číslo nezprovozní: začne fungovat
              až po obnovení LiveKit trunku a S3 mapování pro <code>agent</code>
              . Stav té obnovy je vidět níže.
            </p>
            <DidRoutingStatus
              savedDid={initialPhoneDid}
              lastSynced={initialLastSynced}
              edited={phoneDid.trim() !== initialPhoneDid}
            />
          </Field>
        )}
        {!isDoctor && (
          <Field label="Operátor (SIP)">
            <select
              value={sipCarrier}
              onChange={(e) => {
                const next = e.target.value;
                setSipCarrier(next);
                // The save sends sipReferHost: null for every non-IPEX carrier, and the input unmounts with it. Drop
                // the local value at the same moment so state matches what is written; otherwise `dirty` compares a
                // stale host against the now-empty stored one and the panel never returns to a saved state.
                if (next !== "ipex") {
                  setSipReferHost("");
                }
              }}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
            >
              <option value="ipex">IPEX</option>
              <option value="didww">DIDWW</option>
              <option value="odorik">Odorik</option>
            </select>
            <p className="mt-1 text-[11px] text-gray-500">
              Operátor, který doručuje výše uvedené číslo. Určuje způsob
              přepojení hovoru: IPEX a DIDWW používají SIP REFER, Odorik tónovou
              volbu (DTMF). Změna se projeví po přegenerování promptů.
            </p>
          </Field>
        )}
        {!isDoctor && (
          <Field label="Verze asistentky">
            <select
              value={agentMode}
              onChange={(e) => setAgentMode(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
            >
              <option value="scripted">V1 — řízený scénář</option>
              <option value="realtime">V2 — konverzační agent</option>
            </select>
            <p className="mt-1 text-[11px] text-gray-500">
              V1 vede volajícího pevným scénářem a zapíše vzkaz. V2 vede
              přirozený rozhovor a sám zakládá požadavky. Přepnutí nic nemaže:
              nastavení obou verzí se uchovává zvlášť, takže se lze kdykoli
              vrátit. Při prvním přepnutí na V2 se předvyplní výchozí nastavení
              agenta, které pak upravíte na záložce Agent.
            </p>
          </Field>
        )}
        {!isDoctor && sipCarrier === "ipex" && (
          <Field label="Refer-To doména (jen IPEX)">
            <input
              type="text"
              value={sipReferHost}
              onChange={(e) => setSipReferHost(e.target.value)}
              placeholder="emmymedical.voip"
              className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm placeholder:text-gray-300 focus:border-[#1a2b6b] focus:outline-none"
              autoComplete="off"
              spellCheck={false}
            />
            <p className="mt-1 text-[11px] text-gray-500">
              Autentizační realm trunku u IPEX. Každý zákaznický trunk má svůj
              vlastní; ústředna odmítne přepojení na doménu, kterou nezná.
              Prázdné pole použije výchozí hodnotu <code>emmymedical.voip</code>{" "}
              (DEMO linka).
            </p>
          </Field>
        )}
      </div>
    </Section>
  );
};

/** Practice-level voice + speed. Sits at the top of the Hlášky page so the two levers that affect every prompt are
 *  obvious and co-located with the text the user is editing. Mutation re-renders all prompts after save — hash-check
 *  in the renderer skips prompts whose text+voice+speed didn't change, so this is safe to call aggressively.
 */
const VoiceSettingsPanel = ({ practice }: { practice: PracticeFull }) => {
  const qc = useQueryClient();
  const [voiceId, setVoiceId] = useState(practice.voiceId);
  const [voiceSpeed, setVoiceSpeed] = useState(practice.voiceSpeed);

  const mutation = useMutation({
    mutationFn: async () => {
      await api.updatePractice(practice.id, {
        displayName: practice.displayName,
        enabled: practice.enabled,
        voiceId,
        voiceSpeed: clamp(voiceSpeed, SPEED_MIN, SPEED_MAX),
      });
      await api.renderPrompts(practice.id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["practices"] });
      pollPracticeUntilRenderedOrTimeout(qc, practice.id);
    },
  });

  const dirty =
    voiceId !== practice.voiceId || voiceSpeed !== practice.voiceSpeed;

  return (
    <Section
      title="Hlas a rychlost"
      actions={<SaveButton mutation={mutation} dirty={dirty} />}
    >
      <p className="mb-4 text-xs text-gray-500">
        Základní hlas ordinace. Používá se pro hlavní jazyk a pro každý další
        jazyk, který nemá vlastní hlas nastavený níže v sekci{" "}
        <strong>Jazyky</strong>. Rychlost platí pro všechny jazyky. Po uložení
        se dotčené hlášky automaticky přegenerují.
      </p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Hlas">
          {/* No `inheritLabel`, so the picker never yields null here: the practice voice is the one every language
              falls back to and cannot itself be unset. */}
          <VoicePicker
            voiceId={voiceId}
            onChange={(id) => id && setVoiceId(id)}
          />
        </Field>
        <Field label={`Rychlost (${voiceSpeed.toFixed(2)}×)`}>
          <input
            type="range"
            min={SPEED_MIN}
            max={SPEED_MAX}
            step={0.05}
            value={voiceSpeed}
            onChange={(e) => setVoiceSpeed(Number(e.target.value))}
            className="w-full"
          />
          <div className="mt-1 flex justify-between text-[10px] text-gray-400">
            <span>{SPEED_MIN.toFixed(1)}×</span>
            <span>1,0× (přirozeně)</span>
            <span>{SPEED_MAX.toFixed(1)}×</span>
          </div>
        </Field>
      </div>
    </Section>
  );
};

/** V014 — per-practice tuning for how long the V1 (scripted) bot waits before it responds. Only rendered for scripted
 *  practices (see PracticeMessagesPage); the three knobs are inert on the realtime path. Patch-style save: we send the
 *  current top-level fields unchanged plus the three STT deltas, and the backend COALESCEs the STT values. Re-render is
 *  triggered so the new values reach the agent via the manifest (no TTS re-render happens when only these change). */
const SttTimingPanel = ({ practice }: { practice: PracticeFull }) => {
  const qc = useQueryClient();
  const [endTimeout, setEndTimeout] = useState(practice.speechEndTimeout);
  const [startTimeout, setStartTimeout] = useState(practice.speechStartTimeout);
  const [sensitivity, setSensitivity] = useState(
    practice.endpointingSensitivity,
  );

  const mutation = useMutation({
    mutationFn: async () => {
      await api.updatePractice(practice.id, {
        displayName: practice.displayName,
        enabled: practice.enabled,
        voiceId: practice.voiceId,
        voiceSpeed: practice.voiceSpeed,
        // No phoneDid / sipCarrier / sipReferHost: this panel does not manage them, and omitting a key now leaves the
        // stored value alone. Echoing `practice.phoneDid` back was the old defence against the field being clobbered;
        // it is worse than omission under patch semantics, because a stale cached null reads as an explicit "clear".
        speechEndTimeout: clamp(endTimeout, SPEECH_END_MIN, SPEECH_END_MAX),
        speechStartTimeout: clamp(
          startTimeout,
          SPEECH_START_MIN,
          SPEECH_START_MAX,
        ),
        endpointingSensitivity: sensitivity,
      });
      await api.renderPrompts(practice.id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["practices"] });
      pollPracticeUntilRenderedOrTimeout(qc, practice.id);
    },
  });

  const dirty =
    endTimeout !== practice.speechEndTimeout ||
    startTimeout !== practice.speechStartTimeout ||
    sensitivity !== practice.endpointingSensitivity;

  return (
    <Section
      title="Načasování rozpoznávání řeči"
      actions={<SaveButton mutation={mutation} dirty={dirty} />}
    >
      <p className="mb-4 text-xs text-gray-500">
        Určuje, jak dlouho Sestra Emmy čeká, než začne mluvit. Delší pauza po
        řeči = trpělivější bot, který neskáče do řeči; kratší = svižnější
        reakce. Platí jen pro skriptovaný režim.
      </p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label={`Pauza po dořečení (${endTimeout.toFixed(1)} s)`}>
          <input
            type="number"
            min={SPEECH_END_MIN}
            max={SPEECH_END_MAX}
            step={0.1}
            value={endTimeout}
            onChange={(e) => setEndTimeout(Number(e.target.value))}
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
          />
          <span className="mt-1 block text-[10px] text-gray-400">
            {SPEECH_END_MIN.toFixed(1)}–{SPEECH_END_MAX.toFixed(1)} s. Hlavní
            ovladač „trpělivosti“ — ticho po řeči, než se pacient považuje za
            domluveného.
          </span>
        </Field>
        <Field label={`Čekání na začátek řeči (${Math.round(startTimeout)} s)`}>
          <input
            type="number"
            min={SPEECH_START_MIN}
            max={SPEECH_START_MAX}
            step={1}
            value={startTimeout}
            onChange={(e) => setStartTimeout(Number(e.target.value))}
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
          />
          <span className="mt-1 block text-[10px] text-gray-400">
            {SPEECH_START_MIN}–{SPEECH_START_MAX} s. Jak dlouho čekat, než
            pacient vůbec začne mluvit.
          </span>
        </Field>
        <Field label="Citlivost ukončení řeči">
          <select
            value={sensitivity}
            onChange={(e) => setSensitivity(e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
          >
            {ENDPOINTING_SENSITIVITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Section>
  );
};

const IdentificationSection = ({ practice }: { practice: PracticeFull }) => {
  const qc = useQueryClient();
  const initialConfirm = practice.identificationConfirmEnabled ?? false;
  const initialRetry = practice.identificationRetryEnabled ?? true;
  const initialTrace = practice.identificationTraceEnabled ?? false;
  const [confirmEnabled, setConfirmEnabled] = useState(initialConfirm);
  const [retryEnabled, setRetryEnabled] = useState(initialRetry);
  const [traceEnabled, setTraceEnabled] = useState(initialTrace);
  const dirty =
    confirmEnabled !== initialConfirm ||
    retryEnabled !== initialRetry ||
    traceEnabled !== initialTrace;

  const mutation = useMutation({
    mutationFn: async () => {
      await api.updatePractice(practice.id, {
        displayName: practice.displayName,
        enabled: practice.enabled,
        voiceId: practice.voiceId,
        voiceSpeed: practice.voiceSpeed,
        identificationConfirmEnabled: confirmEnabled,
        identificationRetryEnabled: retryEnabled,
        identificationTraceEnabled: traceEnabled,
      });
      await api.renderPrompts(practice.id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["practices"] });
      pollPracticeUntilRenderedOrTimeout(qc, practice.id);
    },
  });

  return (
    <Section
      title="Identifikace pacienta"
      actions={<SaveButton mutation={mutation} dirty={dirty} />}
    >
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={confirmEnabled}
          onChange={(e) => setConfirmEnabled(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          Potvrzovat identifikaci pacienta s volajícím
          <span className="block text-xs text-gray-500">
            Když Hlas najde pacienta podle jména a data narození, zeptá se
            volajícího, zda je to správný pacient. Hovor se prodlouží o pár
            sekund a zvýší se náklady na syntézu řeči. Bez potvrzení se
            požadavek přiřadí, jen když datum narození sedí přesně a jméno
            jasně odpovídá jediné kartě.
          </span>
        </span>
      </label>
      <label className="mt-3 flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={retryEnabled}
          onChange={(e) => setRetryEnabled(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          Doptat se na neúplnou identifikaci
          <span className="block text-xs text-gray-500">
            Když volající neřekne příjmení nebo celé datum narození, Hlas se
            jednou zeptá znovu. Znění dotazu se upravuje v systémových zprávách
            (identification-birth-date-retry).
          </span>
        </span>
      </label>
      <label className="mt-3 flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={traceEnabled}
          onChange={(e) => setTraceEnabled(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          Ladicí výpis identifikace v požadavku
          <span className="block text-xs text-gray-500">
            Do interního komentáře požadavku se připíše, které karty Hlas
            zvažoval, jak blízko bylo jméno a proč požadavek přiřadil. Výpis
            jmenuje i jiné pacienty se stejným datem narození, proto je určen
            jen pro testování.
          </span>
        </span>
      </label>
    </Section>
  );
};

/** Searchable combobox for ElevenLabs voices. Mirrors TicketTypeCombobox: typeahead filter, arrow keys + Enter + Esc,
 *  primary label (voice name) + secondary italic-grey (the voice category — premade/cloned/generated/professional).
 *  Shows every voice the API key has access to — no filtering by category so nothing is hidden by accident.
 */
const VoicePicker = ({
  voiceId,
  onChange,
  inheritLabel,
}: {
  voiceId: string | null;
  onChange: (id: string | null) => void;
  /** When set, the list gains a first entry meaning "no voice of its own" and `voiceId` may be null. Used by the
   *  per-language pickers, where inheriting the practice voice is both the default and usually the right answer. */
  inheritLabel?: string;
}) => {
  const query = useQuery({
    queryKey: ["elevenlabs-voices"],
    queryFn: api.listElevenLabsVoices,
    staleTime: 5 * 60 * 1000,
  });

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);

  if (query.isError) {
    return (
      <div>
        <input
          value={voiceId ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder="ID hlasu (seznam se nepodařilo načíst)"
          className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm focus:border-[#1a2b6b] focus:outline-none"
        />
        <p className="mt-1 text-xs text-red-600">
          Seznam hlasů se nepodařilo načíst: {(query.error as Error).message}
        </p>
      </div>
    );
  }

  const voices = query.data ?? [];
  const selected = voices.find((v) => v.voiceId === voiceId);
  const filtered = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return voices;
    return voices.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        (v.category?.toLowerCase().includes(q) ?? false) ||
        v.voiceId.toLowerCase().includes(q),
    );
  })();

  const pick = (id: string | null) => {
    onChange(id);
    setSearchQuery("");
    setOpen(false);
  };

  // While the voice list is loading, hide the raw voiceId / "⚠ …" fallback so the input shows just the loading
  // placeholder — otherwise the saved UUID flashes as "garbage" for the 1-2 seconds it takes ElevenLabs to respond.
  const inputDisplay = open
    ? searchQuery
    : selected
      ? selected.name
      : query.isPending
        ? ""
        : voiceId
          ? `⚠ ${voiceId} (není v seznamu)`
          : (inheritLabel ?? "");

  return (
    <div className="relative">
      <input
        value={inputDisplay}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setOpen(true);
          setHighlighted(0);
        }}
        onFocus={() => {
          setSearchQuery("");
          setOpen(true);
          setHighlighted(0);
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlighted((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            if (filtered[highlighted]) pick(filtered[highlighted].voiceId);
            else if (inheritLabel && filtered.length === 0) pick(null);
          } else if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
            (e.target as HTMLInputElement).blur();
          }
        }}
        disabled={query.isPending}
        placeholder={
          query.isPending ? "Načítám hlasy…" : "— vyberte / hledat —"
        }
        className="w-full truncate rounded border border-gray-300 bg-white px-2 py-1.5 text-xs focus:border-[#1a2b6b] focus:outline-none disabled:bg-gray-50"
      />
      {/* Always rendered, even when empty. A category line that appears only on some voices made one language's row
          taller than the rest, and a list of rows that do not share a height reads as broken rather than as informative. */}
      <div className="pointer-events-none mt-0.5 h-[13px] truncate text-[10px] italic text-gray-400">
        {!open && selected?.category ? selected.category : ""}
      </div>
      {open && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full min-w-[20rem] overflow-auto rounded border border-gray-300 bg-white shadow-lg">
          {inheritLabel && (
            <li
              onMouseDown={(e) => {
                e.preventDefault();
                pick(null);
              }}
              className={`cursor-pointer border-b border-gray-100 px-2.5 py-1.5 text-xs ${
                voiceId === null ? "font-medium" : ""
              }`}
            >
              <div className="text-gray-800">{inheritLabel}</div>
            </li>
          )}
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-xs text-gray-500">
              {voices.length === 0
                ? "Žádné hlasy nejsou dostupné"
                : "Žádné výsledky"}
            </li>
          )}
          {filtered.map((v, idx) => {
            const isSelected = v.voiceId === voiceId;
            const isHighlighted = idx === highlighted;
            return (
              <li
                key={v.voiceId}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(v.voiceId);
                }}
                onMouseEnter={() => setHighlighted(idx)}
                className={`cursor-pointer px-2.5 py-1.5 text-xs ${
                  isHighlighted ? "bg-[#1a2b6b]/10" : ""
                } ${isSelected ? "font-medium" : ""}`}
              >
                <div className="truncate text-gray-800">{v.name}</div>
                {v.category && (
                  <div className="truncate text-[10px] italic text-gray-400">
                    {v.category}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// --- Emmy connection: credentials + facility ---

const EmmyConnectionSection = ({ practice }: { practice: PracticeFull }) => {
  const isDoctor = useIsDoctor();
  const [showCredsModal, setShowCredsModal] = useState(false);
  const credsConfigured = !!practice.emmyCredentialsStoredAt;

  // Emmy wiring (credentials + facility mapping) is admin-only per PRD-010 Stage 2. Hide the whole section for
  // DoctorUsers — BE also 403s the mutations as defence-in-depth.
  if (isDoctor) return null;

  return (
    <Section title="Emmy">
      {!credsConfigured ? (
        <div>
          <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            Emmy není propojené. Bez propojení nebudou hovory doručovat tikety.
          </div>
          <button
            onClick={() => setShowCredsModal(true)}
            className="rounded bg-[#1a2b6b] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0f1d52]"
          >
            Propojit s Emmy
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <EmmyCredsStatus
            practiceId={practice.id}
            storedAt={practice.emmyCredentialsStoredAt ?? null}
            username={practice.emmyCognitoUsername ?? null}
            onEdit={() => setShowCredsModal(true)}
          />
          <EmmyFacilityPicker practice={practice} />
        </div>
      )}
      {showCredsModal && (
        <EmmyCredsModal
          practiceId={practice.id}
          onClose={() => setShowCredsModal(false)}
        />
      )}
    </Section>
  );
};

const EmmyCredsStatus = ({
  practiceId,
  storedAt,
  username,
  onEdit,
}: {
  practiceId: string;
  storedAt: string | null;
  username: string | null;
  onEdit: () => void;
}) => {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => api.deleteEmmyCreds(practiceId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["practice", practiceId] }),
  });

  const storedDate = storedAt
    ? new Date(storedAt).toLocaleDateString("cs-CZ")
    : "—";

  return (
    <div className="flex items-center justify-between rounded border border-green-300 bg-green-50 p-3 text-sm">
      <div className="flex flex-col gap-0.5 text-green-800">
        <span>Propojeno (uloženo {storedDate})</span>
        {username && (
          <span className="text-xs text-green-700">
            Servisní účet: <span className="font-mono">{username}</span>
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <button
          onClick={onEdit}
          className="rounded border border-[#1a2b6b] px-3 py-1 text-xs text-[#1a2b6b] hover:bg-[#1a2b6b] hover:text-white"
        >
          Změnit
        </button>
        <button
          onClick={() => {
            if (
              window.confirm(
                "Odpojit Emmy? Uložené přihlašovací údaje budou smazány.",
              )
            ) {
              del.mutate();
            }
          }}
          disabled={del.isPending}
          className="rounded border border-red-500 px-3 py-1 text-xs text-red-600 hover:bg-red-500 hover:text-white disabled:opacity-50"
        >
          {del.isPending ? "Odpojuji…" : "Odpojit"}
        </button>
      </div>
    </div>
  );
};

const EmmyCredsModal = ({
  practiceId,
  onClose,
}: {
  practiceId: string;
  onClose: () => void;
}) => {
  const qc = useQueryClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const save = useMutation({
    mutationFn: () => api.putEmmyCreds(practiceId, username, password),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["practice", practiceId] });
      onClose();
    },
  });

  const canSubmit =
    username.length > 0 && password.length > 0 && !save.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded border border-gray-200 bg-white p-6 shadow-lg">
        <h4 className="mb-4 text-sm font-semibold text-[#1a2b6b]">
          Propojit s Emmy
        </h4>
        <div className="space-y-3">
          <Field label="Uživatelské jméno">
            <input
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Heslo">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
          {save.isError && (
            <p className="text-xs text-red-600">
              {(save.error as Error).message}
            </p>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100"
          >
            Zrušit
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={!canSubmit}
            className="rounded bg-[#1a2b6b] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0f1d52] disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {save.isPending ? "Ukládám…" : "Uložit"}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Facility picker only — per-type TicketType mapping lives inside RequestTypesSection. Saves by calling
 *  `saveEmmyWiring` with the currently-persisted `ticketTypeIdByTypeId` unchanged (so changing the facility doesn't
 *  clobber request-type mappings; those are managed below). */
const EmmyFacilityPicker = ({ practice }: { practice: PracticeFull }) => {
  const qc = useQueryClient();
  const facilitiesQ = useQuery({
    queryKey: ["practice", practice.id, "emmy-facilities"],
    queryFn: () => api.listEmmyFacilities(practice.id),
  });

  const initialFacilityId = practice.emmy?.facilityId ?? "";
  const [facilityId, setFacilityId] = useState(initialFacilityId);
  useEffect(() => setFacilityId(initialFacilityId), [initialFacilityId]);

  useEffect(() => {
    if (!facilityId && facilitiesQ.data && facilitiesQ.data.length === 1) {
      setFacilityId(facilitiesQ.data[0].id);
    }
  }, [facilitiesQ.data, facilityId]);

  const save = useMutation({
    mutationFn: () =>
      api.saveEmmyWiring(
        practice.id,
        facilityId,
        practice.emmy?.ticketTypeIdByTypeId ?? {},
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["practice", practice.id] }),
  });

  const dirty = facilityId !== initialFacilityId;

  if (facilitiesQ.isPending)
    return <p className="text-xs text-gray-500">Načítám ordinace z Emmy…</p>;
  if (facilitiesQ.isError) {
    return (
      <p className="text-xs text-red-600">
        Chyba Emmy: {(facilitiesQ.error as Error).message}
      </p>
    );
  }

  const facilities = facilitiesQ.data ?? [];

  return (
    <div className="space-y-2">
      <Field label="Ordinace (Emmy facility)">
        {facilities.length === 1 ? (
          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
            {facilities[0].displayName}{" "}
            <code className="ml-2 text-[10px] text-gray-400">
              {facilities[0].id}
            </code>
          </div>
        ) : (
          <select
            value={facilityId}
            onChange={(e) => setFacilityId(e.target.value)}
            className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">— vyberte —</option>
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>
                {f.displayName}
              </option>
            ))}
          </select>
        )}
      </Field>
      {dirty && facilityId && (
        <div className="flex justify-end">
          <SaveButton mutation={save} dirty={dirty} />
        </div>
      )}
    </div>
  );
};

// --- Danger zone: hard-delete the practice ---
//
// Admin-only. Lives on the Facility page per the user's April 2026 ask to restore it after PRD-010 Stage 2 removed it.
// The confirmation modal requires the admin to re-type the practice id — same pattern as the original `2c8bbf7` commit.

const DangerZoneSection = ({ practice }: { practice: PracticeFull }) => {
  const isDoctor = useIsDoctor();
  const [showModal, setShowModal] = useState(false);
  if (isDoctor) return null;
  return (
    <section className="rounded border border-red-200 bg-red-50/40 p-6">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-red-700">
        Nebezpečná zóna
      </h3>
      <p className="mb-4 text-sm text-gray-700">
        Smazání ordinace je nevratné. Odstraní veškerou konfiguraci (hlasové
        zprávy, typy požadavků, ordinační hodiny, dovolené, propojení s Emmy).
        Hovory zaznamenané touto ordinací zůstanou a brání smazání — v takovém
        případě ordinaci pouze deaktivujte v sekci Obecné.
      </p>
      <button
        onClick={() => setShowModal(true)}
        className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
      >
        Smazat ordinaci…
      </button>
      {showModal && (
        <DeletePracticeModal
          practice={practice}
          onClose={() => setShowModal(false)}
        />
      )}
    </section>
  );
};

const DeletePracticeModal = ({
  practice,
  onClose,
}: {
  practice: PracticeFull;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmText, setConfirmText] = useState("");

  const del = useMutation({
    mutationFn: () => api.deletePractice(practice.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["practices"] });
      navigate("/practices");
    },
  });

  const canDelete = confirmText === practice.id && !del.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded border border-red-200 bg-white p-6 shadow-lg">
        <h4 className="mb-3 text-base font-semibold text-red-700">
          Smazat ordinaci {practice.displayName}?
        </h4>
        <p className="mb-3 text-sm text-gray-700">
          Tato akce je <strong>nevratná</strong>. Smaže veškerá nastavení této
          ordinace. Hovory zaznamenané v systému zůstanou a smazání znemožní.
        </p>
        <p className="mb-2 text-sm text-gray-700">
          Pro potvrzení napište ID ordinace:{" "}
          <code className="font-mono">{practice.id}</code>
        </p>
        <input
          autoFocus
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={practice.id}
          className="mb-2 w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm focus:border-red-600 focus:outline-none"
        />
        {del.isError && (
          <p className="mb-2 text-xs text-red-600">
            {(del.error as Error).message}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={del.isPending}
            className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            Zrušit
          </button>
          <button
            onClick={() => del.mutate()}
            disabled={!canDelete}
            className="rounded bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {del.isPending ? "Mažu…" : "Smazat ordinaci"}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Prompts ---

/** Which agent generations actually play a given prompt.
 *
 * The realtime agent speaks through the model and has no `_play` call site, so the scripted flow's lines never
 * sound on a V2 call. Two exceptions are decided before the V1/V2 fork and therefore apply to both: the
 * out-of-hours closure and the scheduled-transfer handoff.
 *
 * Only display is filtered. The save is replace-all, so the hidden rows stay in the payload — dropping them would
 * delete a practice's scripted prompts the moment it was switched to V2.
 */
type PromptAudience = "all" | "scripted";

const PRACTICE_PROMPT_SLOTS: {
  promptId: string;
  label: string;
  description: string;
  audience: PromptAudience;
}[] = [
  {
    promptId: "intro",
    label: "Úvod",
    description:
      "Uvítací zpráva, kterou Emmy přehraje, jakmile se volající spojí.",
    audience: "scripted",
  },
  {
    promptId: "specification",
    label: "Specifikace",
    description: "Vyzve volajícího, aby stručně popsal, co potřebuje.",
    audience: "scripted",
  },
  {
    promptId: "closing",
    label: "Závěr",
    description: "Rozloučení před zavěšením.",
    audience: "scripted",
  },
  {
    promptId: "out-of-hours",
    label: "Mimo ordinační hodiny",
    description:
      "Zpráva pro hovory mimo ordinační dobu — volajícího se krátce poučí a zavěsí.",
    audience: "all",
  },
  {
    promptId: "language-menu",
    label: "Nabídka jazyka",
    description:
      "Věta, kterou volající uslyší v tomto jazyce a která mu řekne, kterou číslici má stisknout. Existuje jen u dalších jazyků, ne u hlavního — nabídka se přehrává jednou, ještě než si volající jazyk vybere.",
    audience: "scripted",
  },
  {
    promptId: "transfer-offer",
    label: "Nabídka přepojení",
    description:
      "Zazní po úvodu, když je přepojení zapnuté. Devítku lze stisknout kdykoli během hovoru, nejen hned po této hlášce.",
    audience: "scripted",
  },
  {
    promptId: "transfer-b2b-question",
    label: "Přepojení — dotaz na pacienta (B2B)",
    description:
      "Jen u neznámých čísel, když je zapnutý B2B dotaz. Zazní hned po úvodu. „Ne“ nebo odpověď s klíčovým slovem přepojí na B2B číslo, jinak hovor pokračuje identifikací pacienta.",
    audience: "scripted",
  },
  {
    promptId: "transfer-confirm",
    label: "Přepojení — potvrzení",
    description:
      "Vysvětlí, že přepojení je jen pro vážné ohrožení života nebo zdraví, a vyžádá si „ano“. Odmítnutí vrátí volajícího zpět do běžného průběhu hovoru.",
    audience: "scripted",
  },
  {
    promptId: "transfer-handoff",
    label: "Přepojení — předání",
    description:
      "Krátká hláška těsně před samotným přepojením. Přehraje se u všech způsobů přepojení.",
    audience: "scripted",
  },
  {
    promptId: "transfer-scheduled",
    label: "Přepojení — automatické podle rozvrhu",
    description:
      "Zazní v oknech automatického přepojení, kdy se hovor přepojí bez zásahu volajícího.",
    audience: "all",
  },
  {
    promptId: "info-offer",
    label: "Nabídka nahrané informace",
    description:
      "Zazní po nabídce přepojení, když je nahraná informace zapnutá. Musí volajícímu říct, že má stisknout osmičku — jinak o té možnosti neví.",
    audience: "scripted",
  },
  {
    promptId: "info-message",
    label: "Nahraná informace",
    description:
      "Co volající uslyší po stisku osmičky. Tím hovor končí — nevzniká žádný požadavek a do Emmy se nic nepředává. Dokud je prázdná, osmička se volajícím vůbec nenabízí.",
    audience: "scripted",
  },
];

/** Languages we can currently offer. Kept short and explicit rather than open-ended: every entry here has to have a
 *  usable ElevenLabs voice and Chirp streaming coverage, and neither is guaranteed just because a code exists. */
const OFFERABLE_LANGUAGES: { code: string; label: string }[] = [
  { code: "cs", label: "Čeština" },
  { code: "sk", label: "Slovenština" },
  { code: "en", label: "Angličtina" },
  { code: "de", label: "Němčina" },
  { code: "pl", label: "Polština" },
  { code: "uk", label: "Ukrajinština" },
  { code: "ru", label: "Ruština" },
  { code: "hu", label: "Maďarština" },
  { code: "vi", label: "Vietnamština" },
];

/** Main plus four extras. Mirrors `PracticeLanguage.MaxLanguagesPerPractice` on the API side, which is where it is
 *  actually enforced — no CHECK can count sibling rows. */
const MAX_LANGUAGES = 5;

/** "Press 8 for the recorded information" (V023, scripted only).
 *
 *  The switch lives here rather than beside the transfer controls because the thing it switches on is a message on
 *  this page, and an admin who has just written one looks for the way to turn it on where they wrote it.
 *
 *  The switch is NOT disabled while the message is unwritten, unlike the language menu's. It was, and that made the
 *  obvious order of work impossible: turn it on, then write it. The server already enforces the real condition — the
 *  manifest withholds the offer until the message has text, whatever this flag says — so nothing can reach a caller
 *  early, and the amber banner below says what is still missing.
 */
const InfoMessageSection = ({ practice }: { practice: PracticeFull }) => {
  const qc = useQueryClient();
  const mainLanguage =
    practice.languages?.find((l) => l.isMain)?.language ?? "cs";
  const message = practice.prompts.find(
    (p) => p.promptId === "info-message" && p.language === mainLanguage,
  );
  const written = (message?.czechText ?? "").trim().length > 0;

  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      api.setInfoMessageEnabled(practice.id, enabled),
    onSuccess: () => pollPracticeUntilRenderedOrTimeout(qc, practice.id),
  });

  const enabled = practice.infoMessageEnabled ?? false;

  return (
    <Section title="Nahraná informace">
      <p className="mb-3 text-xs text-gray-500">
        Volající stiskne osmičku, vyslechne si nahranou informaci a hovor tím
        končí. Nevzniká žádný požadavek a do Emmy se nic nepředává — hodí se na
        to, na co ordinace odpovídá pořád stejně: časté dotazy, očkování proti
        chřipce, letní dovolená. Text napíšete níže do hlášek{" "}
        <strong>Nahraná informace</strong> a{" "}
        <strong>Nabídka nahrané informace</strong>.
      </p>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          disabled={toggle.isPending}
          onChange={(e) => toggle.mutate(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          Nabízet volajícím nahranou informaci
          <span className="block text-xs text-gray-500">
            Osmička funguje kdykoli během hovoru, nejen hned po nabídce.
            {!written &&
              " Zapnout ji můžete i teď — volajícím se ale nabídne až ve chvíli, kdy níže vyplníte text hlášky Nahraná informace."}
          </span>
        </span>
      </label>

      {enabled && !written && (
        <p className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Nahraná informace je zapnutá, ale hláška je prázdná, takže se
          volajícím osmička vůbec nenabízí. Napište text níže a nechte hlášky
          vygenerovat.
        </p>
      )}
    </Section>
  );
};

const LanguagesSection = ({ practice }: { practice: PracticeFull }) => {
  const qc = useQueryClient();
  const languages = practice.languages ?? [];
  const main = languages.find((l) => l.isMain);
  const extras = languages.filter((l) => !l.isMain);
  const isScripted = practice.agentMode === "scripted";

  const takenDigits = new Set(
    extras.map((l) => l.menuDigit).filter((d): d is number => d != null),
  );
  // 1-7 only. 8 became the information message in V023 and 9 has been the transfer offer since V018; mirrors
  // `PracticeLanguage.MinMenuDigit`/`MaxMenuDigit`, which is where it is actually enforced.
  const freeDigit = [1, 2, 3, 4, 5, 6, 7].find((d) => !takenDigits.has(d));
  const available = OFFERABLE_LANGUAGES.filter(
    (o) => !languages.some((l) => l.language === o.code),
  );

  const [adding, setAdding] = useState(available[0]?.code ?? "");
  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["practice", practice.id] });

  const addLanguage = useMutation({
    mutationFn: () =>
      api.addLanguage(practice.id, {
        language: adding,
        // Realtime practices have no keypad menu, so their languages carry no digit.
        menuDigit: isScripted ? (freeDigit ?? null) : null,
        enabled: false,
        summaryLanguage: "main",
      }),
    onSuccess: () => pollPracticeUntilRenderedOrTimeout(qc, practice.id),
  });

  const updateLanguage = useMutation({
    mutationFn: ({
      code,
      patch,
    }: {
      code: string;
      patch: Partial<PracticeLanguage>;
    }) => {
      const row = languages.find((l) => l.language === code);
      return api.updateLanguage(practice.id, code, {
        language: code,
        menuDigit:
          patch.menuDigit !== undefined ? patch.menuDigit : row?.menuDigit,
        enabled:
          patch.enabled !== undefined ? patch.enabled : (row?.enabled ?? false),
        summaryLanguage:
          patch.summaryLanguage ?? row?.summaryLanguage ?? "main",
        // Three-way, and the `undefined` case matters: the field is omitted entirely unless this edit is about the
        // voice, so a checkbox toggle cannot clear an override. `null` is the explicit "inherit the practice voice".
        ...(patch.voiceId !== undefined ? { voiceId: patch.voiceId } : {}),
      });
    },
    // A voice change re-synthesises that language's audio. The render hash covers the voice, so the other languages
    // are skipped — but the caller still hears the old voice until this finishes, which is why it is worth waiting on.
    onSuccess: (_data, variables) =>
      variables.patch.voiceId !== undefined
        ? pollPracticeUntilRenderedOrTimeout(qc, practice.id)
        : refresh(),
  });

  const removeLanguage = useMutation({
    mutationFn: (code: string) => api.deleteLanguage(practice.id, code),
    onSuccess: refresh,
  });

  const toggleMenu = useMutation({
    mutationFn: (enabled: boolean) =>
      api.setLanguageMenuEnabled(practice.id, enabled),
    onSuccess: () => pollPracticeUntilRenderedOrTimeout(qc, practice.id),
  });

  const anyOfferable = extras.some((l) => l.enabled && l.menuDigit != null);

  return (
    <Section title="Jazyky">
      <p className="mb-3 text-xs text-gray-500">
        Hlavní jazyk ordinace je{" "}
        <strong>{main?.language.toUpperCase() ?? "?"}</strong>. Přidáním dalšího
        jazyka se hlášky automaticky přeloží a vygenerují, ale volajícím se
        nabídnou až ve chvíli, kdy jazyk zapnete — nejdřív si překlad
        poslechněte na záložce Hlasové zprávy.
      </p>
      <p className="mb-3 text-xs text-gray-500">
        Každý jazyk může mít <strong>vlastní hlas</strong>. Hlas naklonovaný z
        české nahrávky umí cizí jazyk vyslovit, ale zpravidla s přízvukem — pro
        angličtinu nebo maďarštinu se proto obvykle vyplatí vybrat hlas rodilého
        mluvčího. Změna hlasu přegeneruje hlášky pouze u toho jazyka.
      </p>

      {isScripted && (
        <label className="mb-3 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={practice.languageMenuEnabled ?? false}
            disabled={toggleMenu.isPending || !anyOfferable}
            onChange={(e) => toggleMenu.mutate(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Přehrávat volajícím nabídku jazyků
            <span className="block text-xs text-gray-500">
              Po úvodní hlášce Emmy nabídne jazyky a počká na stisk číslice.
              Nabídka zazní jednou; stisk spustí celý hovor znovu ve zvoleném
              jazyce.
              {!anyOfferable &&
                " Nejdřív přidejte a zapněte alespoň jeden další jazyk."}
            </span>
          </span>
        </label>
      )}

      {/* A grid rather than a flex row: the columns have to line up between languages, and the main language has no
          "nabízet volajícím" checkbox — under `flex-wrap` its absence shifted every field after it, so no two rows
          agreed on where the voice was. Fixed tracks, and the main row leaves its cell empty. */}
      <div className="min-w-0 overflow-x-auto">
        <div className="min-w-[44rem]">
          <div className="grid grid-cols-[2.5rem_4.25rem_4.5rem_minmax(16rem,1fr)_10rem_5rem] items-end gap-x-3 px-3 pb-2 text-[10px] font-medium uppercase tracking-wide text-gray-400">
            <span>Jazyk</span>
            <span className="text-center">{isScripted ? "Klávesa" : ""}</span>
            <span className="text-center">Nabízet</span>
            <span>Hlas</span>
            <span>Souhrn v</span>
            <span />
          </div>

          <div className="space-y-2.5">
            {languages.map((l) => (
              <div
                key={l.language}
                className="grid grid-cols-[2.5rem_4.25rem_4.5rem_minmax(16rem,1fr)_10rem_5rem] items-start gap-x-3 rounded border border-gray-200 px-3 py-3 text-sm"
              >
                <span className="pt-1.5 font-semibold text-[#1a2b6b]">
                  {l.language.toUpperCase()}
                </span>
                <span
                  className="pt-1.5 text-center text-xs text-gray-500"
                  title={
                    l.isMain
                      ? "Hlavní jazyk — volající nemusí mačkat nic"
                      : "Číslice, kterou volající stiskne"
                  }
                >
                  {l.isMain ? "—" : isScripted ? (l.menuDigit ?? "—") : "—"}
                </span>

                <span className="pt-1.5 text-center">
                  {!l.isMain && (
                    <input
                      type="checkbox"
                      aria-label={`Nabízet ${l.language.toUpperCase()} volajícím`}
                      title="Nabízet tento jazyk volajícím"
                      checked={l.enabled}
                      disabled={updateLanguage.isPending}
                      onChange={(e) =>
                        updateLanguage.mutate({
                          code: l.language,
                          patch: { enabled: e.target.checked },
                        })
                      }
                    />
                  )}
                </span>

                <span className="min-w-0">
                  {l.isMain ? (
                    <span className="block truncate rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-500">
                      hlas ordinace (výše)
                    </span>
                  ) : (
                    <VoicePicker
                      voiceId={l.voiceId ?? null}
                      inheritLabel="stejný jako hlas ordinace"
                      onChange={(voice) =>
                        updateLanguage.mutate({
                          code: l.language,
                          patch: { voiceId: voice },
                        })
                      }
                    />
                  )}
                </span>

                <select
                  value={l.summaryLanguage}
                  disabled={updateLanguage.isPending || l.isMain}
                  onChange={(e) =>
                    updateLanguage.mutate({
                      code: l.language,
                      patch: {
                        summaryLanguage: e.target.value as "main" | "spoken",
                      },
                    })
                  }
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="main">hlavním jazyce</option>
                  <option value="spoken">jazyce hovoru</option>
                </select>

                <span className="pt-0.5 text-right">
                  {!l.isMain && (
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Odebrat ${l.language.toUpperCase()}? Smažou se i všechny přeložené hlášky tohoto jazyka.`,
                          )
                        )
                          removeLanguage.mutate(l.language);
                      }}
                      disabled={removeLanguage.isPending}
                      className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:text-gray-300"
                    >
                      Odebrat
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {languages.length < MAX_LANGUAGES && available.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1 text-sm"
          >
            {available.map((o) => (
              <option key={o.code} value={o.code}>
                {o.label} ({o.code})
              </option>
            ))}
          </select>
          <button
            onClick={() => addLanguage.mutate()}
            disabled={
              addLanguage.isPending ||
              !adding ||
              (isScripted && freeDigit == null)
            }
            className="rounded bg-[#1a2b6b] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0f1d52] disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {addLanguage.isPending ? "Přidávám a překládám…" : "Přidat jazyk"}
          </button>
          {addLanguage.isError && (
            <span className="text-xs text-red-600">
              {(addLanguage.error as Error).message}
            </span>
          )}
          {addLanguage.data?.translationError && (
            <span className="text-xs text-amber-700">
              Jazyk přidán, ale překlad se nepovedl:{" "}
              {addLanguage.data.translationError}. Texty zůstaly v hlavním
              jazyce — upravte je ručně nebo zkuste „Přeložit znovu“.
            </span>
          )}
        </div>
      )}
      {languages.length >= MAX_LANGUAGES && (
        <p className="mt-3 text-xs text-gray-500">
          Dosažen limit {MAX_LANGUAGES} jazyků na ordinaci.
        </p>
      )}
    </Section>
  );
};

const PromptsSection = ({
  practiceId,
  initial,
  agentMode,
  languages,
}: {
  practiceId: string;
  initial: Prompt[];
  agentMode: string;
  languages: PracticeLanguage[];
}) => {
  const qc = useQueryClient();
  const isDoctor = useIsDoctor();

  const mainLanguage = languages.find((l) => l.isMain)?.language ?? "cs";
  // One tab per language, main first. Disabled languages are shown too: reviewing a
  // translation before switching it on is the whole point of the enable flag.
  const tabs = useMemo(
    () =>
      [...languages].sort(
        (a, b) =>
          Number(b.isMain) - Number(a.isMain) ||
          (a.menuDigit ?? 99) - (b.menuDigit ?? 99) ||
          a.language.localeCompare(b.language),
      ),
    [languages],
  );
  const [activeLanguage, setActiveLanguage] = useState(mainLanguage);
  useEffect(() => {
    if (!tabs.some((l) => l.language === activeLanguage))
      setActiveLanguage(mainLanguage);
  }, [tabs, activeLanguage, mainLanguage]);

  const isMainTab = activeLanguage === mainLanguage;

  const initialBySlot = useMemo<Prompt[]>(
    () =>
      PRACTICE_PROMPT_SLOTS.map(
        (slot) =>
          initial.find(
            (p) =>
              p.promptId === slot.promptId && p.language === activeLanguage,
          ) ?? {
            promptId: slot.promptId,
            czechText: "",
            voiceId: "",
            s3Key: "",
            language: activeLanguage,
          },
      ),
    [initial, activeLanguage],
  );

  // Rows outside PRACTICE_PROMPT_SLOTS, in this language only, passed through unchanged on save. Scoped to the
  // language because the save replaces exactly one language's rows — sweeping in another's would recreate them
  // under the wrong language.
  const extraPrompts = useMemo<Prompt[]>(
    () =>
      initial.filter(
        (p) =>
          p.language === activeLanguage &&
          !PRACTICE_PROMPT_SLOTS.some((s) => s.promptId === p.promptId),
      ),
    [initial, activeLanguage],
  );

  const isRealtime = agentMode === "realtime";

  const [prompts, setPrompts] = useState<Prompt[]>(initialBySlot);
  useEffect(() => setPrompts(initialBySlot), [initialBySlot]);

  const updateAt = (i: number, patch: Partial<Prompt>) =>
    setPrompts((ps) =>
      ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)),
    );

  /** Save this language's prompts + trigger a render. The Lambda hash-checks each prompt, so only the one(s) whose
   *  text actually changed get re-synthesized — effectively a "save and regenerate just this message" UX per row. */
  const saveAndRender = useMutation({
    // `promptId` is only used by the UI to show a per-row pending spinner via `saveAndRender.variables`.
    mutationFn: async (promptId: string) => {
      await api.updatePrompts(
        practiceId,
        activeLanguage,
        // The text is now the admin's, whatever it started as, so the unreviewed badge goes with it.
        [...prompts, ...extraPrompts].map((p) => ({
          ...p,
          language: activeLanguage,
          autoTranslated: false,
        })),
      );
      await api.renderPrompts(practiceId);
      return promptId;
    },
    // render-prompts runs async — poll repeatedly so renderedAt refreshes without a page refresh.
    onSuccess: () => pollPracticeUntilRenderedOrTimeout(qc, practiceId),
  });

  const retranslate = useMutation({
    mutationFn: () => api.translateLanguage(practiceId, activeLanguage),
    onSuccess: () => pollPracticeUntilRenderedOrTimeout(qc, practiceId),
  });

  return (
    <Section title="Hlasové zprávy">
      <p className="mb-3 text-xs text-gray-500">
        Hlasové zprávy, které Emmy přehrává během hovoru. Tlačítko{" "}
        <em>Uložit a přegenerovat</em> u každé zprávy uloží texty vybraného
        jazyka a přegeneruje audio pouze u těch, které se od posledního
        vygenerování změnily.
      </p>
      {tabs.length > 1 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {tabs.map((l) => (
            <button
              key={l.language}
              onClick={() => setActiveLanguage(l.language)}
              className={`rounded px-3 py-1 text-xs font-medium ${
                l.language === activeLanguage
                  ? "bg-[#1a2b6b] text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {l.language.toUpperCase()}
              {l.isMain ? " (hlavní)" : l.enabled ? "" : " — vypnuto"}
            </button>
          ))}
          {!isMainTab && (
            <button
              onClick={() => retranslate.mutate()}
              disabled={retranslate.isPending}
              title="Znovu přeloží texty z hlavního jazyka. Věty, které jste už ručně upravili, zůstanou beze změny."
              className="ml-auto rounded border border-gray-300 px-3 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
            >
              {retranslate.isPending ? "Překládám…" : "Přeložit znovu"}
            </button>
          )}
        </div>
      )}
      {retranslate.isError && (
        <p className="mb-3 text-xs text-red-600">
          {(retranslate.error as Error).message}
        </p>
      )}
      {isRealtime && (
        <p className="mb-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
          Ordinace běží na V2, kde odpovědi tvoří sám model — texty řízeného
          scénáře se proto nepřehrávají a nejsou zde zobrazeny. Zůstávají
          uložené a vrátí se, jakmile ordinaci přepnete zpět na V1. Níže jsou
          jen zprávy, které V2 skutečně používá:{" "}
          {isDoctor
            ? "chování asistentky změníte na záložce Souhrn."
            : "obsah agenta upravíte na záložce Agent."}
        </p>
      )}
      <div className="space-y-3">
        {prompts.map((p, i) => {
          const slot = PRACTICE_PROMPT_SLOTS[i];
          // Filtered here rather than out of `prompts`, so the index stays aligned
          // and every row remains in the replace-all save.
          if (isRealtime && slot.audience !== "all") return null;
          // The menu line announces which digit selects THIS language, so it exists
          // only on the extras. The main language is what a caller gets by pressing
          // nothing at all.
          if (slot.promptId === "language-menu" && isMainTab) return null;
          const dirty = p.czechText !== initialBySlot[i].czechText;
          const isPendingForThis =
            saveAndRender.isPending &&
            saveAndRender.variables === slot.promptId;
          const unreviewed = p.autoTranslated === true && !p.reviewedAt;
          return (
            <div
              key={`${activeLanguage}:${slot.promptId}`}
              className="rounded border border-gray-200 p-3"
            >
              <div className="mb-2 flex items-start justify-between gap-2">
                <div>
                  <h4 className="text-sm font-semibold text-[#1a2b6b]">
                    {slot.label}
                  </h4>
                  <p className="text-xs text-gray-500">{slot.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  {unreviewed && (
                    <span
                      title="Strojový překlad, který zatím nikdo neupravil. Poslechněte si ho, než jazyk zapnete."
                      className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                    >
                      nezkontrolováno
                    </span>
                  )}
                  <code className="text-[10px] text-gray-400">
                    {slot.promptId}
                  </code>
                </div>
              </div>
              <textarea
                value={p.czechText}
                onChange={(e) => updateAt(i, { czechText: e.target.value })}
                rows={3}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
              />
              <div className="mt-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">
                    {isPendingForThis
                      ? "— přegeneruji…"
                      : p.renderedAt
                        ? `vygenerováno ${new Date(p.renderedAt).toLocaleString("cs-CZ")}`
                        : "— zatím nevygenerováno"}
                  </span>
                  {!isPendingForThis && (
                    <PromptListenButton
                      practiceId={practiceId}
                      language={activeLanguage}
                      prompt={p}
                    />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {saveAndRender.isError &&
                    saveAndRender.variables === slot.promptId && (
                      <span className="text-xs text-red-600">
                        {(saveAndRender.error as Error).message}
                      </span>
                    )}
                  <button
                    onClick={() => saveAndRender.mutate(slot.promptId)}
                    disabled={
                      !dirty ||
                      saveAndRender.isPending ||
                      p.czechText.trim().length === 0
                    }
                    title={
                      p.czechText.trim().length === 0
                        ? "Text zprávy nesmí být prázdný"
                        : !dirty
                          ? "Žádné neuložené změny"
                          : "Uložit tuto zprávu a přegenerovat její audio"
                    }
                    className="rounded bg-[#1a2b6b] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0f1d52] disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {isPendingForThis ? "Ukládám…" : "Uložit a přegenerovat"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const PromptListenButton = ({
  practiceId,
  language,
  prompt,
}: {
  practiceId: string;
  language: string;
  prompt: Prompt;
}) => {
  const [url, setUrl] = useState<string | null>(null);
  // Reset the cached presigned URL whenever the prompt is re-rendered, or the editor switches language, so a second
  // play picks up the right audio without needing a page refresh.
  useEffect(() => setUrl(null), [prompt.renderedAt, language]);

  const mutation = useMutation({
    mutationFn: () =>
      api.getPromptAudioUrl(practiceId, language, prompt.promptId),
    onSuccess: (r) => setUrl(r.url),
  });

  // Parallel to VacationBlockListenButton: hide entirely until rendered (the timestamp line already says so), inline
  // audio once loaded, and a Přehrát button otherwise.
  if (!prompt.renderedAt) return null;
  if (url) return <audio controls src={url} className="h-8" />;

  return (
    <button
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending || !prompt.promptId}
      className="rounded border border-[#1a2b6b] px-3 py-1 text-xs text-[#1a2b6b] hover:bg-[#1a2b6b] hover:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent"
    >
      {mutation.isPending ? "Načítám…" : "▶ Přehrát"}
    </button>
  );
};

// --- Request types (unified: label + criteria + Emmy TicketType mapping per row) ---

const slugifyTypeId = (s: string): string =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);

/** Build the initial list from `practice.requestTypes`, synthesising a default `jine` row if one isn't present.
 *  Ensures the catch-all is always there and is always last. */
const ensureJine = (types: RequestType[]): RequestType[] => {
  const nonJine = types
    .filter((t) => t.typeId !== JINE_TYPE_ID)
    .sort((a, b) => a.position - b.position);
  const existingJine = types.find((t) => t.typeId === JINE_TYPE_ID);
  const jine: RequestType = existingJine ?? {
    typeId: JINE_TYPE_ID,
    displayName: "Jiné",
    criteriaDescription: "Cokoliv jiného, co nezapadá do ostatních kategorií.",
    position: 0,
  };
  return [...nonJine, { ...jine, position: nonJine.length }].map((t, i) => ({
    ...t,
    position: i,
  }));
};

/** Searchable combobox for Emmy TicketTypes. Filters by both `displayName` (patient-facing name) and `internalName`
 *  (admin-side label — often the shorter, canonical name). Shows both lines per option: displayName prominent,
 *  internalName italic-grey underneath. Keyboard-nav (↑ ↓ Enter Esc) + click; open on focus.
 */
const TicketTypeCombobox = ({
  value,
  onChange,
  options,
  disabled,
  isStale,
  isLoading = false,
}: {
  value: string;
  onChange: (id: string) => void;
  options: EmmyTicketType[];
  disabled: boolean;
  isStale: boolean;
  /** TicketTypes are being fetched from Emmy — don't flag saved mappings as stale (we can't know yet) and disable
   *  editing until we have the real list. Prevents the "⚠ TicketType nedostupný" flash on page load. */
  isLoading?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);

  const selected = options.find((o) => o.id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.displayName.toLowerCase().includes(q) ||
        (o.internalName?.toLowerCase().includes(q) ?? false),
    );
  }, [options, query]);

  useEffect(() => {
    if (highlighted >= filtered.length)
      setHighlighted(Math.max(0, filtered.length - 1));
  }, [filtered.length, highlighted]);

  const pick = (id: string) => {
    onChange(id);
    setQuery("");
    setOpen(false);
  };

  const inputDisplay = open
    ? query
    : selected
      ? selected.displayName
      : isLoading
        ? ""
        : isStale && value
          ? "⚠ TicketType nedostupný"
          : "";

  return (
    <div className="relative">
      <input
        value={inputDisplay}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlighted(0);
        }}
        onFocus={() => {
          setQuery("");
          setOpen(true);
          setHighlighted(0);
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlighted((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            if (filtered[highlighted]) pick(filtered[highlighted].id);
          } else if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder={
          isLoading
            ? "Načítám TicketTypes z Emmy…"
            : selected
              ? selected.displayName
              : "— vyberte / hledat —"
        }
        disabled={disabled || isLoading}
        className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none disabled:bg-gray-50"
      />
      {!open &&
        selected?.internalName &&
        selected.internalName !== selected.displayName && (
          <div className="pointer-events-none mt-0.5 truncate text-[11px] italic text-gray-400">
            {selected.internalName}
          </div>
        )}
      {open && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full min-w-[20rem] overflow-auto rounded border border-gray-300 bg-white shadow-lg">
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-xs text-gray-500">Žádné výsledky</li>
          )}
          {filtered.map((o, idx) => {
            const isSelected = o.id === value;
            const isHighlighted = idx === highlighted;
            return (
              <li
                key={o.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o.id);
                }}
                onMouseEnter={() => setHighlighted(idx)}
                className={`cursor-pointer px-3 py-2 text-sm ${
                  isHighlighted ? "bg-[#1a2b6b]/10" : ""
                } ${isSelected ? "font-medium" : ""}`}
              >
                <div className="text-gray-800">{o.displayName}</div>
                {o.internalName && o.internalName !== o.displayName && (
                  <div className="text-[11px] italic text-gray-400">
                    {o.internalName}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const RequestTypesSection = ({ practice }: { practice: PracticeFull }) => {
  const qc = useQueryClient();
  const facilityId = practice.emmy?.facilityId ?? "";
  const initialMapping = practice.emmy?.ticketTypeIdByTypeId ?? {};

  const ticketTypesQ = useQuery({
    queryKey: ["practice", practice.id, "emmy-ticket-types", facilityId],
    queryFn: () => api.listEmmyTicketTypes(practice.id, facilityId),
    enabled: facilityId.length > 0,
  });

  const initialTypes = useMemo(
    () => ensureJine(practice.requestTypes),
    [practice.requestTypes],
  );
  const [types, setTypes] = useState<RequestType[]>(initialTypes);
  const [mapping, setMapping] =
    useState<Record<string, string>>(initialMapping);
  const [createForTypeId, setCreateForTypeId] = useState<string | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  useEffect(() => setTypes(initialTypes), [initialTypes]);
  useEffect(
    () => setMapping(initialMapping),
    [practice.emmy?.ticketTypeIdByTypeId],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // requestTypes + emmy.ticketTypeIdByTypeId both land in the manifest the agent reads at call start. Chain
  // renderPrompts so edits here propagate to S3 — hash-check on per-prompt text means no TTS re-run, just a
  // manifest rebuild.
  const save = useMutation({
    mutationFn: async () => {
      const ordered = withPositions(moveJineLast(types));
      await api.updateRequestTypes(practice.id, ordered);
      // Keep mappings only for currently-present typeIds.
      const filtered: Record<string, string> = {};
      for (const t of ordered)
        if (mapping[t.typeId]) filtered[t.typeId] = mapping[t.typeId];
      if (facilityId)
        await api.saveEmmyWiring(practice.id, facilityId, filtered);
      await api.renderPrompts(practice.id);
    },
    onSuccess: () => pollPracticeUntilRenderedOrTimeout(qc, practice.id),
  });

  const typesDirty =
    JSON.stringify(withPositions(moveJineLast(types))) !==
    JSON.stringify(initialTypes);
  const mappingDirty =
    JSON.stringify(mapping) !== JSON.stringify(initialMapping);
  const dirty = typesDirty || mappingDirty;

  const ticketTypes = ticketTypesQ.data ?? [];
  const knownTicketTypeIds = new Set(ticketTypes.map((t) => t.id));

  const addRow = () =>
    setTypes((t) => {
      const withoutJine = t.filter((r) => r.typeId !== JINE_TYPE_ID);
      const jine = t.find((r) => r.typeId === JINE_TYPE_ID);
      const newRow: RequestType = {
        typeId: "",
        displayName: "",
        criteriaDescription: "",
        position: withoutJine.length,
      };
      const all = jine
        ? [...withoutJine, newRow, jine]
        : [...withoutJine, newRow];
      return all.map((r, i) => ({ ...r, position: i }));
    });

  const updateRow = (i: number, patch: Partial<RequestType>) =>
    setTypes((t) =>
      t.map((row, idx) => (idx === i ? { ...row, ...patch } : row)),
    );

  const removeRow = (i: number) =>
    setTypes((t) => {
      const next = t
        .filter((_, idx) => idx !== i)
        .map((r, idx) => ({ ...r, position: idx }));
      // If the removed row had a mapping, drop it too so we don't leak stale entries.
      setMapping((m) => {
        const removed = t[i]?.typeId;
        if (!removed || !(removed in m)) return m;
        const rest: Record<string, string> = {};
        for (const k of Object.keys(m)) if (k !== removed) rest[k] = m[k];
        return rest;
      });
      return next;
    });

  const moveRow = (i: number, dir: -1 | 1) =>
    setTypes((t) => {
      const j = i + dir;
      if (j < 0 || j >= t.length) return t;
      // jine always stays last
      if (t[i].typeId === JINE_TYPE_ID || t[j].typeId === JINE_TYPE_ID)
        return t;
      const copy = [...t];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy.map((r, idx) => ({ ...r, position: idx }));
    });

  const onDisplayNameBlur = (i: number) => {
    setTypes((t) =>
      t.map((row, idx) =>
        idx === i && row.typeId.length === 0 && row.displayName.length > 0
          ? { ...row, typeId: slugifyTypeId(row.displayName) }
          : row,
      ),
    );
  };

  const onTicketTypeCreated = (typeId: string, tt: EmmyTicketType) => {
    qc.setQueryData<EmmyTicketType[]>(
      ["practice", practice.id, "emmy-ticket-types", facilityId],
      (prev) => (prev ? [...prev, tt] : [tt]),
    );
    setMapping((m) => ({ ...m, [typeId]: tt.id }));
    setCreateForTypeId(null);
  };

  // Save-blocking validation: every row needs a name, slug, and criteria. Emmy mapping is NOT required here —
  // admin can save the types first and come back to pair them with Emmy TicketTypes. Unmapped types surface as
  // a non-blocking warning at the top of the section.
  const rowErrors = (t: RequestType): string[] => {
    const errs: string[] = [];
    if (t.displayName.trim().length === 0) errs.push("název");
    if (t.typeId.trim().length === 0) errs.push("slug");
    if (t.criteriaDescription.trim().length === 0) errs.push("kritéria");
    return errs;
  };

  const allValid = types.every((t) => rowErrors(t).length === 0);
  const savedTypeIds = new Set(practice.requestTypes.map((t) => t.typeId));
  const unmappedTypeIds = facilityId
    ? types.filter((t) => !mapping[t.typeId]).map((t) => t.typeId)
    : [];

  return (
    <Section
      title="Typy požadavků"
      actions={
        <div className="flex items-center gap-3">
          {save.isError && (
            <span className="text-xs text-red-600">
              {(save.error as Error).message}
            </span>
          )}
          {save.isSuccess && (
            <span aria-live="polite" className="text-xs text-green-700">
              Uloženo
            </span>
          )}
          <button
            onClick={() => save.mutate()}
            disabled={!dirty || !allValid || save.isPending}
            title={
              !allValid
                ? "Vyplňte název, ID a kritéria u všech řádků"
                : undefined
            }
            className="rounded bg-[#1a2b6b] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0f1d52] disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {save.isPending ? "Ukládám…" : "Uložit"}
          </button>
        </div>
      }
    >
      <p className="mb-3 text-xs text-gray-500">
        Každý typ má svůj název, kritéria pro klasifikátor a propojení na Emmy
        TicketType. Poslední řádek <code>jine</code> je povinný catch-all —
        nelze smazat; Emmy do něj spadne vše, co se neshoduje s žádným jiným
        typem.
      </p>

      {!facilityId && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
          Nejprve vyberte ordinaci v sekci Emmy výše. Propojení na Emmy
          TicketType lze nastavit až poté.
        </div>
      )}

      {facilityId && ticketTypesQ.isPending && (
        <p className="mb-3 text-xs text-gray-500">
          Načítám TicketTypes z Emmy…
        </p>
      )}
      {facilityId && ticketTypesQ.isError && (
        <p className="mb-3 text-xs text-red-600">
          Chyba Emmy: {(ticketTypesQ.error as Error).message}
        </p>
      )}

      {facilityId && unmappedTypeIds.length > 0 && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
          Nepropojené typy ({unmappedTypeIds.length}):{" "}
          <code>{unmappedTypeIds.join(", ")}</code>. Hovory klasifikované do
          těchto kategorií nevytvoří tiket v Emmy — doplňte Emmy TicketType u
          každého řádku.
        </div>
      )}

      <div className="space-y-3">
        {types.map((t, i) => {
          const isJine = t.typeId === JINE_TYPE_ID;
          const isSaved = savedTypeIds.has(t.typeId) && t.typeId.length > 0;
          const mappedId = mapping[t.typeId] ?? "";
          // Only flag a saved mapping as "stale" once we've actually loaded the TicketTypes list from Emmy — during
          // loading `knownTicketTypeIds` is empty and every mapping would spuriously look unavailable.
          const isStale =
            ticketTypesQ.isSuccess &&
            mappedId.length > 0 &&
            !knownTicketTypeIds.has(mappedId);
          const errs = rowErrors(t);

          return (
            <div
              key={i}
              className={`rounded border ${isJine ? "border-blue-200 bg-blue-50/30" : "border-gray-200"} p-3`}
            >
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="text-xs text-gray-500">
                  {isJine ? (
                    <span className="font-medium text-blue-700">
                      Catch-all (povinné)
                    </span>
                  ) : (
                    <span>Typ {i + 1}</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!isJine && (
                    <>
                      <button
                        onClick={() => moveRow(i, -1)}
                        disabled={i === 0}
                        title="Posunout nahoru"
                        className="text-xs text-gray-500 hover:text-[#1a2b6b] disabled:opacity-30"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveRow(i, 1)}
                        disabled={i >= types.length - 2}
                        title="Posunout dolů"
                        className="text-xs text-gray-500 hover:text-[#1a2b6b] disabled:opacity-30"
                      >
                        ↓
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setDeleteIndex(i)}
                    disabled={isJine}
                    title={
                      isJine
                        ? "Catch-all nelze smazat"
                        : `Smazat ${t.displayName || t.typeId}`
                    }
                    className="ml-2 text-xs text-red-600 hover:underline disabled:cursor-not-allowed disabled:text-gray-300 disabled:no-underline"
                  >
                    ✕ Smazat
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <Field label="Název">
                  <input
                    value={t.displayName}
                    onChange={(e) =>
                      updateRow(i, { displayName: e.target.value })
                    }
                    onBlur={() => onDisplayNameBlur(i)}
                    placeholder="např. Recept"
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="ID (slug)">
                  <input
                    value={t.typeId}
                    onChange={(e) => updateRow(i, { typeId: e.target.value })}
                    readOnly={isSaved || isJine}
                    placeholder="např. recept"
                    title={
                      isJine
                        ? "Catch-all má pevné ID `jine` a nelze ho změnit."
                        : isSaved
                          ? "ID nelze po uložení měnit — propojení na Emmy TicketType by se mohlo tiše rozbít. Pro přejmenování typ smažte a vytvořte nový."
                          : "Automaticky doplněno z názvu po odkliknutí. Krátký stabilní slug (bez diakritiky)."
                    }
                    className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm read-only:cursor-not-allowed read-only:bg-gray-50 read-only:text-gray-600"
                  />
                </Field>
              </div>

              <div className="mt-2">
                <Field label="Kritéria (popis pro klasifikátor)">
                  <textarea
                    value={t.criteriaDescription}
                    onChange={(e) =>
                      updateRow(i, { criteriaDescription: e.target.value })
                    }
                    rows={2}
                    placeholder="Co do této kategorie spadá — pomáhá modelu rozhodnout."
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </Field>
              </div>

              <div className="mt-2">
                <Field label="Emmy TicketType">
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <TicketTypeCombobox
                        value={mappedId}
                        onChange={(id) =>
                          setMapping((m) => ({ ...m, [t.typeId]: id }))
                        }
                        options={ticketTypes}
                        disabled={!facilityId || t.typeId.length === 0}
                        isStale={isStale}
                        isLoading={ticketTypesQ.isPending}
                      />
                      {isStale && (
                        <p className="mt-1 text-xs text-red-600">
                          Propojený Emmy TicketType již není v seznamu aktivních
                          — zvolte jiný nebo vytvořte nový.
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setCreateForTypeId(t.typeId)}
                      disabled={!facilityId || t.typeId.length === 0}
                      title={
                        !facilityId
                          ? "Nejprve vyberte ordinaci v sekci Emmy"
                          : t.typeId.length === 0
                            ? "Nejprve zadejte název"
                            : "Vytvořit nový TicketType v Emmy"
                      }
                      className="whitespace-nowrap rounded border border-[#1a2b6b] px-3 py-2 text-xs text-[#1a2b6b] hover:bg-[#1a2b6b] hover:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent"
                    >
                      Vytvořit v Emmy
                    </button>
                  </div>
                </Field>
              </div>

              {errs.length > 0 && (
                <p className="mt-2 text-xs text-amber-700">
                  Chybí: {errs.join(", ")}.
                </p>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={addRow}
        className="mt-4 rounded border border-[#1a2b6b] px-3 py-1.5 text-xs text-[#1a2b6b] hover:bg-[#1a2b6b] hover:text-white"
      >
        + Přidat typ požadavku
      </button>

      {createForTypeId !== null && (
        <EmmyCreateTicketTypeModal
          practiceId={practice.id}
          facilityId={facilityId}
          prefilledName={
            types.find((t) => t.typeId === createForTypeId)?.displayName ?? ""
          }
          onClose={() => setCreateForTypeId(null)}
          onCreated={(tt) => onTicketTypeCreated(createForTypeId, tt)}
        />
      )}

      {deleteIndex !== null && (
        <ConfirmModal
          title="Smazat typ požadavku?"
          message={`Opravdu chcete smazat "${types[deleteIndex]?.displayName || types[deleteIndex]?.typeId}"? Tato akce nenávratně odpojí mapování na Emmy TicketType.`}
          onConfirm={() => {
            removeRow(deleteIndex);
            setDeleteIndex(null);
          }}
          onClose={() => setDeleteIndex(null)}
        />
      )}
    </Section>
  );
};

const EmmyCreateTicketTypeModal = ({
  practiceId,
  facilityId,
  prefilledName,
  onClose,
  onCreated,
}: {
  practiceId: string;
  facilityId: string;
  prefilledName: string;
  onClose: () => void;
  onCreated: (tt: EmmyTicketType) => void;
}) => {
  const [name, setName] = useState(prefilledName);
  const [shortName, setShortName] = useState("");
  const [patientCategory, setPatientCategory] = useState("");
  const [defaultTriage, setDefaultTriage] = useState("");
  const [defaultCategory, setDefaultCategory] = useState("");
  const [defaultTicketAssignee, setDefaultTicketAssignee] = useState("");

  const patCategoriesQ = useQuery({
    queryKey: ["practice", practiceId, "emmy", "ticket-type-categories"],
    queryFn: () => api.listEmmyTicketTypeCategories(practiceId),
  });
  const triagesQ = useQuery({
    queryKey: ["practice", practiceId, "emmy", "triages"],
    queryFn: () => api.listEmmyTriages(practiceId),
  });
  const ticketCategoriesQ = useQuery({
    queryKey: ["practice", practiceId, "emmy", "ticket-categories"],
    queryFn: () => api.listEmmyTicketCategories(practiceId),
  });
  const assigneesQ = useQuery({
    queryKey: ["practice", practiceId, "emmy", "ticket-assignees"],
    queryFn: () => api.listEmmyTicketAssignees(practiceId),
  });

  const create = useMutation({
    mutationFn: () =>
      api.createEmmyTicketType(practiceId, {
        facilityId,
        name,
        shortName: shortName.trim().length > 0 ? shortName : undefined,
        patientCategory,
        defaultTriage,
        defaultCategory:
          defaultCategory.length > 0 ? defaultCategory : undefined,
        defaultTicketAssignee:
          defaultTicketAssignee.length > 0 ? defaultTicketAssignee : undefined,
      }),
    onSuccess: onCreated,
  });

  const canSubmit =
    name.trim().length > 0 &&
    patientCategory.length > 0 &&
    defaultTriage.length > 0 &&
    !create.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl overflow-auto rounded border border-gray-200 bg-white p-6 shadow-lg">
        <h4 className="mb-1 text-sm font-semibold text-[#1a2b6b]">
          Vytvořit TicketType v Emmy
        </h4>
        <p className="mb-4 text-xs text-gray-500">
          Nový TicketType se založí ve stavu <code>Enabled</code> a ihned se
          objeví v Emmy.
        </p>
        <div className="space-y-3">
          <Field label="Název v Emmy">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Krátký název (volitelné)">
            <input
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              placeholder="např. recept"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Kategorie pacienta (povinné)">
            <LookupComboboxField
              value={patientCategory}
              onChange={setPatientCategory}
              query={patCategoriesQ}
              placeholderWhenEmpty="Žádné kategorie nenalezeny"
            />
          </Field>
          <Field label="Výchozí triage (povinné)">
            <LookupComboboxField
              value={defaultTriage}
              onChange={setDefaultTriage}
              query={triagesQ}
              placeholderWhenEmpty="Žádné triage"
            />
          </Field>
          <Field label="Výchozí kategorie (volitelné)">
            <LookupComboboxField
              value={defaultCategory}
              onChange={setDefaultCategory}
              query={ticketCategoriesQ}
              placeholderWhenEmpty="Žádné kategorie"
              allowClear
            />
          </Field>
          <Field label="Výchozí přiřazený (volitelné)">
            <LookupComboboxField
              value={defaultTicketAssignee}
              onChange={setDefaultTicketAssignee}
              query={assigneesQ}
              placeholderWhenEmpty="Žádní přiřazení nenalezeni"
              allowClear
            />
          </Field>
        </div>
        {create.isError && (
          <p className="mt-3 text-xs text-red-600">
            {(create.error as Error).message}
          </p>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100"
          >
            Zrušit
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={!canSubmit}
            title={
              !canSubmit
                ? "Vyplňte povinná pole: název, kategorie pacienta, triage"
                : undefined
            }
            className="rounded bg-[#1a2b6b] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0f1d52] disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {create.isPending ? "Vytvářím…" : "Vytvořit"}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Thin wrapper that adapts a `useQuery` of `EmmyLookupItem[]` to the searchable combobox, handling loading / error /
 *  empty states. Shared across the four dropdowns in the create-TicketType modal.
 */
const LookupComboboxField = ({
  value,
  onChange,
  query,
  placeholderWhenEmpty,
  allowClear = false,
}: {
  value: string;
  onChange: (id: string) => void;
  query: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    data?: { id: string; name: string }[];
  };
  placeholderWhenEmpty: string;
  allowClear?: boolean;
}) => {
  if (query.isPending) {
    return (
      <input
        disabled
        placeholder="Načítám…"
        className="w-full rounded border border-gray-300 bg-gray-50 px-3 py-2 text-sm"
      />
    );
  }
  if (query.isError) {
    return (
      <p className="text-xs text-red-600">
        Chyba: {(query.error as Error).message}
      </p>
    );
  }
  const options = query.data ?? [];
  return (
    <SimpleCombobox
      value={value}
      onChange={onChange}
      options={options}
      emptyText={placeholderWhenEmpty}
      allowClear={allowClear}
    />
  );
};

/** Minimal searchable combobox for Emmy lookup lists — primary label only, no secondary line. */
const SimpleCombobox = ({
  value,
  onChange,
  options,
  emptyText,
  allowClear,
}: {
  value: string;
  onChange: (id: string) => void;
  options: { id: string; name: string }[];
  emptyText: string;
  allowClear: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);

  const selected = options.find((o) => o.id === value);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, query]);

  const pick = (id: string) => {
    onChange(id);
    setQuery("");
    setOpen(false);
  };

  const inputDisplay = open ? query : selected ? selected.name : "";

  return (
    <div className="relative">
      <input
        value={inputDisplay}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlighted(0);
        }}
        onFocus={() => {
          setQuery("");
          setOpen(true);
          setHighlighted(0);
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlighted((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            if (filtered[highlighted]) pick(filtered[highlighted].id);
          } else if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder={options.length === 0 ? emptyText : "— vyberte / hledat —"}
        disabled={options.length === 0}
        className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none disabled:bg-gray-50"
      />
      {allowClear && value && !open && (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            onChange("");
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600"
          title="Smazat výběr"
        >
          ✕
        </button>
      )}
      {open && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full min-w-[20rem] overflow-auto rounded border border-gray-300 bg-white shadow-lg">
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-xs text-gray-500">
              {options.length === 0 ? emptyText : "Žádné výsledky"}
            </li>
          )}
          {filtered.map((o, idx) => {
            const isSelected = o.id === value;
            const isHighlighted = idx === highlighted;
            return (
              <li
                key={o.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o.id);
                }}
                onMouseEnter={() => setHighlighted(idx)}
                className={`cursor-pointer px-3 py-2 text-sm ${
                  isHighlighted ? "bg-[#1a2b6b]/10" : ""
                } ${isSelected ? "font-medium" : ""}`}
              >
                {o.name}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// --- Opening hours ---

const OpeningHoursSection = ({
  practiceId,
  initial,
}: {
  practiceId: string;
  initial: OpeningHoursBlock[];
}) => {
  const qc = useQueryClient();

  const initialGrid = useMemo(() => toGrid(initial), [initial]);
  const [grid, setGrid] = useState<GridCell[][]>(initialGrid);
  useEffect(() => setGrid(initialGrid), [initialGrid]);

  // Opening hours are carried in the manifest the in-call agent reads on every call. Chain renderPrompts so the save
  // doesn't just update the DB — the agent only sees the change once manifest.json is rebuilt in S3. The Lambda
  // hash-checks each prompt, so no TTS re-synthesis happens; it's effectively a manifest refresh.
  const mutation = useMutation({
    mutationFn: async () => {
      await api.updateOpeningHours(practiceId, fromGrid(grid));
      await api.renderPrompts(practiceId);
    },
    onSuccess: () => pollPracticeUntilRenderedOrTimeout(qc, practiceId),
  });

  const dirty = JSON.stringify(grid) !== JSON.stringify(initialGrid);

  return (
    <Section
      title="Ordinační hodiny"
      actions={
        <SaveButton
          mutation={mutation}
          dirty={dirty}
          blocked={hasGridErrors(grid)}
        />
      }
    >
      <p className="mb-3 text-xs text-gray-500">
        Až tři bloky denně (např. ranní + odpolední ordinace s obědem). Čas
        pište číslem, např. 7, 730 nebo 7:30. Prázdný blok = neaktivní.
      </p>
      <WeeklyBlocksEditor grid={grid} onChange={setGrid} blockLabel="Blok" />
    </Section>
  );
};

// --- V2 agent settings ---

/** Shared save wiring for the agent sections.
 *
 * Each section PUTs only the keys it owns, so two screens edited in the same session cannot overwrite each other —
 * the backend overlays the sections it receives and leaves the rest alone. Every save chains renderPrompts, since the
 * agent reads all of this from the manifest.
 */
const useAgentPatch = (practiceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: PracticeAgentPatch) => {
      await api.updateAgent(practiceId, patch);
      await api.renderPrompts(practiceId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["practice", practiceId] });
      // The plain-language summary is generated from these settings, and the FAQ editor now sits directly under it on
      // Souhrn. Without this the practice edits an answer, sees it saved, and reads a paragraph above it still
      // describing the answer they replaced.
      void qc.invalidateQueries({ queryKey: ["practice-summary", practiceId] });
      pollPracticeUntilRenderedOrTimeout(qc, practiceId);
    },
  });
};

// Gemini's base Live model takes no thinking level; only its extended-thinking variant does.
const EXTENDED_THINKING = "extended-thinking";
const TURN_MODE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "výchozí Azure (podle ticha, 200 ms)" },
  { value: "server_vad", label: "podle ticha (server_vad)" },
  { value: "semantic_vad", label: "podle smyslu řeči (semantic_vad)" },
];
const EAGERNESS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "výchozí (auto)" },
  { value: "low", label: "nízká — nechá volajícího domluvit" },
  { value: "medium", label: "střední" },
  { value: "high", label: "vysoká — odpovídá co nejdřív" },
];
const NOISE_REDUCTION_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "vypnuto" },
  { value: "near_field", label: "blízký mikrofon (telefon u ucha)" },
  { value: "far_field", label: "vzdálený mikrofon (hlasitý odposlech)" },
];
const SENSITIVITY_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "výchozí" },
  { value: "low", label: "nízká" },
  { value: "high", label: "vysoká" },
];

/** What the form holds: strings, so an empty field is "leave the default". */
interface SpeechDetectionForm {
  startSensitivity: string;
  endSensitivity: string;
  prefixPaddingMs: string;
  silenceDurationMs: string;
}

const detectionToForm = (
  d: SpeechDetection | null | undefined,
): SpeechDetectionForm => ({
  startSensitivity: d?.startSensitivity ?? "",
  endSensitivity: d?.endSensitivity ?? "",
  prefixPaddingMs: d?.prefixPaddingMs == null ? "" : String(d.prefixPaddingMs),
  silenceDurationMs:
    d?.silenceDurationMs == null ? "" : String(d.silenceDurationMs),
});

const formToDetection = (f: SpeechDetectionForm): SpeechDetection | null => {
  const ms = (raw: string): number | null => {
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  };
  const d: SpeechDetection = {
    startSensitivity:
      f.startSensitivity === "" ? null : (f.startSensitivity as "low" | "high"),
    endSensitivity:
      f.endSensitivity === "" ? null : (f.endSensitivity as "low" | "high"),
    prefixPaddingMs: ms(f.prefixPaddingMs),
    silenceDurationMs: ms(f.silenceDurationMs),
  };
  const anySet = Object.values(d).some((v) => v !== null);
  return anySet ? d : null;
};

const DEPLOYMENT_HINT: Record<string, ReactNode> = {
  "azure-openai": (
    <>
      Název nasazení v Azure OpenAI. Nasazení stejného jména musí v Azure
      existovat; dnes je nasazený <code>gpt-realtime-2.1</code>.
    </>
  ),
  "vertex-gemini": (
    <>
      Model ve Vertex AI, běží v EU (multiregion <code>eu</code>). Varianta
      extended-thinking uvažuje déle a zpřístupní míru uvažování.
    </>
  ),
  "azure-gpt-live": (
    <>
      Plně duplexní hlasový model v Azure OpenAI: poslouchá a mluví zároveň.
      Vyhledávání a zápisy předává modelu na pozadí, jemuž patří i míra
      uvažování. Nasazení <code>gpt-live-1</code> musí v Azure existovat.
    </>
  ),
};

// A stored deployment missing from the catalogue stays selectable, so the form shows what the agent runs with.
const deploymentOptionsFor = (
  provider: AgentProvider | undefined,
  current: string,
): string[] => {
  if (!provider) {
    return [current];
  }
  if (provider.deployments.includes(current)) {
    return provider.deployments;
  }
  return [current, ...provider.deployments];
};

// A stored voice missing from the catalogue stays selectable, so the form shows what the agent runs with.
const voiceOptionsFor = (
  provider: AgentProvider | undefined,
  current: string,
): string[] => {
  if (!provider) {
    return [current];
  }
  if (provider.voices.includes(current)) {
    return provider.voices;
  }
  return [current, ...provider.voices];
};

const AgentModelSection = ({
  practiceId,
  agent,
}: {
  practiceId: string;
  agent: PracticeAgentSettings;
}) => {
  const mutation = useAgentPatch(practiceId);
  const providers = useQuery({
    queryKey: ["agent-providers"],
    queryFn: api.agentProviders,
    staleTime: Infinity,
  });
  const [provider, setProvider] = useState(agent.model.provider);
  const [voice, setVoice] = useState(agent.model.voice);
  const [deployment, setDeployment] = useState(agent.model.deployment);
  const [effort, setEffort] = useState(agent.model.reasoningEffort ?? "low");
  const [detection, setDetection] = useState(
    detectionToForm(agent.model.speechDetection),
  );
  const [pinLanguage, setPinLanguage] = useState(
    agent.model.pinLanguageCode !== false,
  );
  const [realtime, setRealtime] = useState(realtimeToForm(agent.model));
  const [langDefault, setLangDefault] = useState(agent.language.default);
  const [langAllowed, setLangAllowed] = useState(
    agent.language.allowed.join(", "),
  );

  const selectedProvider = providers.data?.find((p) => p.id === provider);
  const voiceOptions = voiceOptionsFor(selectedProvider, voice);
  const deploymentOptions = deploymentOptionsFor(selectedProvider, deployment);
  const effortOptions = effortOptionsFor(selectedProvider, effort);
  // A practice saved before the provider dropped an effort still carries it. The API refuses it, so say so here
  // instead of letting the save fail; the agent sends the nearest supported level meanwhile.
  const effortUnsupported =
    !!selectedProvider &&
    !reasoningEffortsOf(selectedProvider).includes(effort);

  const switchProvider = (id: string) => {
    setProvider(id);
    const next = providers.data?.find((p) => p.id === id);
    if (next) {
      setDeployment(next.defaultDeployment);
      setVoice(next.voices[0]);
      if (!reasoningEffortsOf(next).includes(effort)) {
        setEffort("low");
      }
    }
  };

  const parsedAllowed = langAllowed
    .split(",")
    .map((x) => x.trim())
    .filter((x) => x.length > 0);

  const realtimeParsed = formToRealtime(realtime);
  const ownSettings = providerSettings(
    provider,
    {
      speechDetection: formToDetection(detection),
      pinLanguageCode: pinLanguage,
    },
    realtimeParsed.value,
  );
  // The stored block read the same way, so a setting stored for another provider, which the save will clear, does not
  // by itself count as an edit.
  const storedSettings = providerSettings(
    agent.model.provider,
    {
      speechDetection: formToDetection(
        detectionToForm(agent.model.speechDetection),
      ),
      pinLanguageCode: agent.model.pinLanguageCode !== false,
    },
    formToRealtime(realtimeToForm(agent.model)).value,
  );
  const dirty =
    provider !== agent.model.provider ||
    JSON.stringify(ownSettings) !== JSON.stringify(storedSettings) ||
    voice !== agent.model.voice ||
    deployment !== agent.model.deployment ||
    effort !== (agent.model.reasoningEffort ?? "low") ||
    langDefault !== agent.language.default ||
    JSON.stringify(parsedAllowed) !== JSON.stringify(agent.language.allowed);

  return (
    <Section
      title="Model a jazyk"
      actions={
        <SaveButton
          mutation={mutation}
          dirty={dirty}
          blocked={
            effortUnsupported ||
            (provider === AZURE_REALTIME_PROVIDER &&
              realtimeParsed.invalid.length > 0)
          }
          onSave={() =>
            mutation.mutate({
              model: {
                ...agent.model,
                provider,
                voice,
                deployment,
                reasoningEffort: effort,
                ...ownSettings,
              },
              language: { default: langDefault, allowed: parsedAllowed },
            })
          }
        />
      }
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Poskytovatel modelu">
          <select
            value={provider}
            onChange={(e) => switchProvider(e.target.value)}
            disabled={!providers.data}
            className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none disabled:bg-gray-50"
          >
            {(providers.data ?? [{ id: provider, label: provider }]).map(
              (p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ),
            )}
          </select>
          <p className="mt-1 text-[11px] text-gray-500">
            Řečový model, který vede hovor. Přepnutí předvyplní nasazení a hlas
            výchozími hodnotami daného poskytovatele.
          </p>
        </Field>
        <Field label="Model">
          <select
            value={deployment}
            onChange={(e) => setDeployment(e.target.value)}
            disabled={!selectedProvider}
            className="w-full rounded border border-gray-300 bg-white px-3 py-2 font-mono text-sm focus:border-[#1a2b6b] focus:outline-none disabled:bg-gray-50"
          >
            {deploymentOptions.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-gray-500">
            {DEPLOYMENT_HINT[provider] ??
              "Název modelu u zvoleného poskytovatele."}
          </p>
        </Field>
        <Field label="Hlas">
          <select
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            disabled={!selectedProvider}
            className="w-full rounded border border-gray-300 bg-white px-3 py-2 font-mono text-sm focus:border-[#1a2b6b] focus:outline-none disabled:bg-gray-50"
          >
            {voiceOptions.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-gray-500">
            Hlasy zvoleného poskytovatele — jiný seznam než hlasy ElevenLabs
            použité u V1.
          </p>
        </Field>
        {((selectedProvider?.hasReasoningEffort ?? true) ||
          deployment.includes(EXTENDED_THINKING)) && (
          <Field label="Míra uvažování">
            <select
              value={effort}
              onChange={(e) => setEffort(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
            >
              {effortOptions.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-500">
              Vyšší úroveň znamená hlubší uvažování, ale i delší odmlky. V
              telefonním hovoru je latence to, co volající vnímá nejvíc — proto
              výchozí <code>low</code>.
              {effortUnsupported && (
                <span className="block text-red-600">
                  Úroveň <code>{effort}</code> tento model nepodporuje; agent ji
                  posílá jako nejbližší podporovanou. Vyberte jinou, jinak
                  nastavení nelze uložit.
                </span>
              )}
              {deployment.includes(EXTENDED_THINKING) && (
                <>
                  {" "}
                  U Gemini zná model jen <code>low</code>, <code>medium</code> a{" "}
                  <code>high</code>; <code>minimal</code> se posílá jako{" "}
                  <code>low</code>, <code>xhigh</code> jako <code>high</code>.
                </>
              )}
            </p>
          </Field>
        )}
        {provider === GEMINI_PROVIDER && (
          <>
            <SpeechDetectionFields value={detection} onChange={setDetection} />
            <Field label="Kód jazyka (Gemini)">
              <label className="flex items-center gap-2 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={pinLanguage}
                  onChange={(e) => setPinLanguage(e.target.checked)}
                />
                Posílat Gemini kód výchozího jazyka
              </label>
              <p className="mt-1 text-[11px] text-gray-500">
                Zapnuto: Gemini dostane pevný kód jazyka, například{" "}
                <code>pl-PL</code>. U <code>gemini-3.8-live</code> je nahlášená
                chyba, kdy pevný kód vede ke sklouzávání do cizího přízvuku nebo
                jiného jazyka; vypnutím necháte jazyk na modelu.
              </p>
            </Field>
          </>
        )}
        {provider === AZURE_REALTIME_PROVIDER && (
          <RealtimeAudioFields
            value={realtime}
            onChange={setRealtime}
            invalid={realtimeParsed.invalid}
          />
        )}
        <Field label="Výchozí jazyk">
          <input
            value={langDefault}
            onChange={(e) => setLangDefault(e.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm focus:border-[#1a2b6b] focus:outline-none"
            placeholder="cs"
            spellCheck={false}
          />
          <p className="mt-1 text-[11px] text-gray-500">
            Jazyk, kterým agent hovor otevře (ISO 639-1).
          </p>
        </Field>
        <Field label="Povolené jazyky">
          <input
            value={langAllowed}
            onChange={(e) => setLangAllowed(e.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm focus:border-[#1a2b6b] focus:outline-none"
            placeholder="cs, en, uk"
            spellCheck={false}
          />
          <p className="mt-1 text-[11px] text-gray-500">
            Oddělené čárkou. Prázdné pole znamená libovolný jazyk, který model
            zvládá. Musí obsahovat výchozí jazyk, nebo být prázdné.
          </p>
        </Field>
      </div>
    </Section>
  );
};

const RealtimeAudioFields = ({
  value,
  onChange,
  invalid,
}: {
  value: RealtimeAudioForm;
  onChange: (next: RealtimeAudioForm) => void;
  invalid: string[];
}) => {
  const set = (key: keyof RealtimeAudioForm, v: string) =>
    onChange({ ...value, [key]: v });
  const selectClass =
    "w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none";
  const inputClass =
    "w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm focus:border-[#1a2b6b] focus:outline-none";
  return (
    <>
      <Field label="Konec repliky (GPT Realtime)">
        <select
          value={value.mode}
          onChange={(e) => set("mode", e.target.value)}
          className={selectClass}
        >
          {TURN_MODE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11px] text-gray-500">
          Jak model pozná, že volající domluvil. Podle smyslu řeči volajícího
          méně přerušuje, na Azure ale zatím není ověřené — vyzkoušejte ho
          zkušebním hovorem.
        </p>
      </Field>
      {value.mode === "semantic_vad" && (
        <Field label="Pohotovost odpovědi (GPT Realtime)">
          <select
            value={value.eagerness}
            onChange={(e) => set("eagerness", e.target.value)}
            className={selectClass}
          >
            {EAGERNESS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-gray-500">
            Jak rychle model odpoví, když usoudí, že volající domluvil.
          </p>
        </Field>
      )}
      {value.mode === "server_vad" && (
        <>
          <Field label="Práh řeči (GPT Realtime)">
            <input
              value={value.threshold}
              onChange={(e) => set("threshold", e.target.value)}
              className={inputClass}
              inputMode="decimal"
              placeholder="0,5"
            />
            <p className="mt-1 text-[11px] text-gray-500">
              Jak hlasitý zvuk se počítá jako řeč, 0 až 1. Vyšší hodnota méně
              reaguje na hluk v pozadí.
            </p>
          </Field>
          <Field label="Předstih před řečí (ms, GPT Realtime)">
            <input
              value={value.prefixPaddingMs}
              onChange={(e) => set("prefixPaddingMs", e.target.value)}
              className={inputClass}
              inputMode="numeric"
              placeholder="300"
            />
            <p className="mt-1 text-[11px] text-gray-500">
              Kolik zvuku před rozpoznaným začátkem řeči model ještě zahrne.{" "}
              {PREFIX_PADDING_RANGE.min} až {PREFIX_PADDING_RANGE.max}.
            </p>
          </Field>
          <Field label="Ticho ukončující repliku (ms, GPT Realtime)">
            <input
              value={value.silenceDurationMs}
              onChange={(e) => set("silenceDurationMs", e.target.value)}
              className={inputClass}
              inputMode="numeric"
              placeholder="200"
            />
            <p className="mt-1 text-[11px] text-gray-500">
              Po jak dlouhém tichu model považuje repliku za dokončenou.
              Výchozích 200 ms je krátkých: model skočí do řeči volajícímu,
              který se jen nadechl. {SILENCE_DURATION_RANGE.min} až{" "}
              {SILENCE_DURATION_RANGE.max}.
            </p>
          </Field>
        </>
      )}
      <Field label="Potlačení šumu (GPT Realtime)">
        <select
          value={value.noiseReduction}
          onChange={(e) => set("noiseReduction", e.target.value)}
          className={selectClass}
        >
          {NOISE_REDUCTION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11px] text-gray-500">
          Odfiltruje hluk v pozadí volajícího, než ho model uslyší.
        </p>
      </Field>
      <Field label="Rychlost řeči (GPT Realtime)">
        <input
          value={value.speed}
          onChange={(e) => set("speed", e.target.value)}
          className={inputClass}
          inputMode="decimal"
          placeholder="1,0"
        />
        <p className="mt-1 text-[11px] text-gray-500">
          {REALTIME_SPEED_RANGE.min} až {REALTIME_SPEED_RANGE.max}. Nižší
          hodnota zpomalí řeč, například pro starší volající.
        </p>
      </Field>
      {invalid.length > 0 && (
        <p className="text-xs text-red-600 md:col-span-2">
          Nelze uložit, pole mají neplatnou hodnotu: {invalid.join(", ")}.
        </p>
      )}
    </>
  );
};

const SpeechDetectionFields = ({
  value,
  onChange,
}: {
  value: SpeechDetectionForm;
  onChange: (next: SpeechDetectionForm) => void;
}) => {
  const set = (key: keyof SpeechDetectionForm, v: string) =>
    onChange({ ...value, [key]: v });
  const selectClass =
    "w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none";
  const inputClass =
    "w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm focus:border-[#1a2b6b] focus:outline-none";
  return (
    <>
      <Field label="Citlivost začátku řeči (Gemini)">
        <select
          value={value.startSensitivity}
          onChange={(e) => set("startSensitivity", e.target.value)}
          className={selectClass}
        >
          {SENSITIVITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11px] text-gray-500">
          Jak snadno model pozná, že volající začal mluvit. Výchozí ponechává
          nastavení Gemini.
        </p>
      </Field>
      <Field label="Citlivost konce řeči (Gemini)">
        <select
          value={value.endSensitivity}
          onChange={(e) => set("endSensitivity", e.target.value)}
          className={selectClass}
        >
          {SENSITIVITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11px] text-gray-500">
          Jak rychle model uzavře repliku volajícího. Výchozí agenta je{" "}
          <code>vysoká</code>: při nižší nechal model dlouhou odpověď volajícího
          bez reakce.
        </p>
      </Field>
      <Field label="Předstih před řečí (ms, Gemini)">
        <input
          value={value.prefixPaddingMs}
          onChange={(e) => set("prefixPaddingMs", e.target.value)}
          className={inputClass}
          inputMode="numeric"
          placeholder="výchozí Gemini"
        />
        <p className="mt-1 text-[11px] text-gray-500">
          Kolik zvuku před rozpoznaným začátkem řeči model ještě zahrne. 0 až
          2000.
        </p>
      </Field>
      <Field label="Ticho ukončující repliku (ms, Gemini)">
        <input
          value={value.silenceDurationMs}
          onChange={(e) => set("silenceDurationMs", e.target.value)}
          className={inputClass}
          inputMode="numeric"
          placeholder="800"
        />
        <p className="mt-1 text-[11px] text-gray-500">
          Po jak dlouhém tichu model považuje repliku za dokončenou. Výchozí
          agenta je 800 ms; Google doporučuje 500 až 800. Povolený rozsah 100 až
          5000.
        </p>
      </Field>
    </>
  );
};

const AgentPermissionsSection = ({
  practiceId,
  agent,
  requestTypes,
}: {
  practiceId: string;
  agent: PracticeAgentSettings;
  requestTypes: RequestType[];
}) => {
  const mutation = useAgentPatch(practiceId);
  const byType = useMemo(() => {
    const m = new Map(agent.requestTypePermissions.map((p) => [p.typeId, p]));
    // Types with no stored permission row default to no access, which is what the
    // agent's deterministic gate does with a missing entry.
    return requestTypes.map((rt) => ({
      typeId: rt.typeId,
      displayName: rt.displayName,
      canCreate: m.get(rt.typeId)?.canCreate ?? false,
      canModify: m.get(rt.typeId)?.canModify ?? false,
    }));
  }, [agent.requestTypePermissions, requestTypes]);

  const [perms, setPerms] = useState(byType);
  useEffect(() => setPerms(byType), [byType]);

  const dirty = JSON.stringify(perms) !== JSON.stringify(byType);

  const toggle = (typeId: string, key: "canCreate" | "canModify") =>
    setPerms((ps) =>
      ps.map((p) => (p.typeId === typeId ? { ...p, [key]: !p[key] } : p)),
    );

  return (
    <Section
      title="Oprávnění k typům požadavků"
      actions={
        <SaveButton
          mutation={mutation}
          dirty={dirty}
          onSave={() =>
            mutation.mutate({
              requestTypePermissions: perms.map(
                ({ typeId, canCreate, canModify }) => ({
                  typeId,
                  canCreate,
                  canModify,
                }),
              ),
            })
          }
        />
      }
    >
      <p className="mb-3 text-xs text-gray-500">
        Co smí agent u jednotlivých typů udělat. Typ bez zaškrtnutého políčka
        agent nezaloží ani neupraví, i když o něj volající výslovně požádá.
        Samotné typy a jejich popisy se spravují na záložce Typy požadavků.
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
            <th className="py-2">Typ</th>
            <th className="py-2">Smí založit</th>
            <th className="py-2">Smí upravit</th>
          </tr>
        </thead>
        <tbody>
          {perms.map((p) => (
            <tr key={p.typeId} className="border-t border-gray-100">
              <td className="py-2 pr-4 text-gray-700">
                {p.displayName}{" "}
                <code className="text-[11px] text-gray-400">{p.typeId}</code>
              </td>
              <td className="py-2 pr-4">
                <input
                  type="checkbox"
                  checked={p.canCreate}
                  onChange={() => toggle(p.typeId, "canCreate")}
                  className="h-4 w-4 rounded border-gray-300"
                />
              </td>
              <td className="py-2 pr-4">
                <input
                  type="checkbox"
                  checked={p.canModify}
                  onChange={() => toggle(p.typeId, "canModify")}
                  className="h-4 w-4 rounded border-gray-300"
                />
              </td>
            </tr>
          ))}
          {perms.length === 0 && (
            <tr>
              <td colSpan={3} className="py-3 text-xs text-gray-500">
                Ordinace zatím nemá žádné typy požadavků.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Section>
  );
};

/** Optional-tier tools. Mirrors the Python registry; the core tools are always bound and are deliberately absent,
 *  since naming one here is rejected by the agent's own loader. */
const OPTIONAL_TOOLS: { name: string; label: string }[] = [
  {
    name: "list_patient_open_tickets",
    label: "Přehled otevřených požadavků pacienta",
  },
  {
    name: "add_internal_message_to_ticket",
    label: "Doplnění zprávy k existujícímu požadavku",
  },
  { name: "list_available_slots", label: "Nabídka volných termínů" },
  { name: "create_appointment", label: "Objednání termínu" },
  { name: "reschedule_appointment", label: "Přesunutí termínu" },
  { name: "cancel_appointment", label: "Zrušení termínu" },
  { name: "transfer_call", label: "Přepojení hovoru" },
  {
    name: "verify_patient_birth_number_suffix",
    label: "Ověření koncovky rodného čísla",
  },
];

const AgentToolsSection = ({
  practiceId,
  agent,
}: {
  practiceId: string;
  agent: PracticeAgentSettings;
}) => {
  const mutation = useAgentPatch(practiceId);
  const [tools, setTools] = useState(agent.tools);
  useEffect(() => setTools(agent.tools), [agent.tools]);

  const dirty =
    JSON.stringify([...tools].sort()) !==
    JSON.stringify([...agent.tools].sort());

  const toggle = (name: string) =>
    setTools((t) =>
      t.includes(name) ? t.filter((x) => x !== name) : [...t, name],
    );

  return (
    <Section
      title="Volitelné nástroje"
      actions={
        <SaveButton
          mutation={mutation}
          dirty={dirty}
          onSave={() => mutation.mutate({ tools })}
        />
      }
    >
      <p className="mb-3 text-xs text-gray-500">
        Co všechno smí agent během hovoru udělat nad rámec základních
        schopností. Základní nástroje (identifikace pacienta, založení
        požadavku, ukončení hovoru) má vždy a nedají se vypnout. Přepojení
        hovoru navíc vyžaduje vyplněné číslo pro přepojení.
      </p>
      <div className="space-y-2">
        {OPTIONAL_TOOLS.map((t) => (
          <label key={t.name} className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={tools.includes(t.name)}
              onChange={() => toggle(t.name)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">
              {t.label}{" "}
              <code className="text-[11px] text-gray-400">{t.name}</code>
            </span>
          </label>
        ))}
      </div>
      {tools.some((t) => !OPTIONAL_TOOLS.some((o) => o.name === t)) && (
        <p className="mt-2 text-[11px] text-amber-600">
          Nastavení obsahuje nástroj, který tato verze aplikace nezná:{" "}
          <code>
            {tools
              .filter((t) => !OPTIONAL_TOOLS.some((o) => o.name === t))
              .join(", ")}
          </code>
          . Uložením se odebere.
        </p>
      )}
    </Section>
  );
};

/** Exported because it is the one V2 setting a practice still edits directly: the answers are written in the words a
 *  receptionist would use, and typing one is quicker than describing it. The Souhrn page renders it below the summary;
 *  the admin Agent tab renders it alongside the technical sections. */
export const PracticeAgentFaqSection = ({
  practiceId,
  agent,
}: {
  practiceId: string;
  agent: PracticeAgentSettings;
}) => {
  const mutation = useAgentPatch(practiceId);
  const [faq, setFaq] = useState(agent.faq);
  useEffect(() => setFaq(agent.faq), [agent.faq]);

  const dirty = JSON.stringify(faq) !== JSON.stringify(agent.faq);
  const incomplete = faq.some(
    (e) => e.question.trim() === "" || e.answer.trim() === "",
  );

  const update = (
    i: number,
    patch: Partial<{ question: string; answer: string }>,
  ) => setFaq((f) => f.map((e, j) => (j === i ? { ...e, ...patch } : e)));

  return (
    <Section
      title="Časté dotazy"
      actions={
        <SaveButton
          mutation={mutation}
          dirty={dirty && !incomplete}
          onSave={() => mutation.mutate({ faq })}
        />
      }
    >
      <p className="mb-3 text-xs text-gray-500">
        Odpovědi, které agent zná a může rovnou použít, aniž by zakládal
        požadavek — otevírací doba, parkování, co si vzít s sebou. Formulujte je
        tak, jak by je řekl člověk; agent je nečte doslova, ale vychází z nich.
      </p>
      <div className="space-y-3">
        {faq.map((entry, i) => (
          <div key={i} className="rounded border border-gray-200 p-3">
            <input
              value={entry.question}
              onChange={(e) => update(i, { question: e.target.value })}
              placeholder="Otázka"
              className="mb-2 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
            />
            <textarea
              value={entry.answer}
              onChange={(e) => update(i, { answer: e.target.value })}
              placeholder="Odpověď"
              rows={2}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setFaq((f) => f.filter((_, j) => j !== i))}
              className="mt-2 text-xs text-red-600 hover:underline"
            >
              Odebrat
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setFaq((f) => [...f, { question: "", answer: "" }])}
        className="mt-3 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
      >
        Přidat dotaz
      </button>
      {incomplete && (
        <p className="mt-2 text-[11px] text-amber-600">
          Každý dotaz musí mít vyplněnou otázku i odpověď, jinak nelze uložit.
        </p>
      )}
    </Section>
  );
};

const AgentPersonaSection = ({
  practiceId,
  agent,
}: {
  practiceId: string;
  agent: PracticeAgentSettings;
}) => {
  const mutation = useAgentPatch(practiceId);
  const [persona, setPersona] = useState(agent.persona);
  const [rules, setRules] = useState(agent.rules.join("\n"));
  useEffect(() => {
    setPersona(agent.persona);
    setRules(agent.rules.join("\n"));
  }, [agent.persona, agent.rules]);

  const parsedRules = rules
    .split("\n")
    .map((r) => r.trim())
    .filter((r) => r.length > 0);

  const dirty =
    persona !== agent.persona ||
    JSON.stringify(parsedRules) !== JSON.stringify(agent.rules);

  return (
    <Section
      title="Osobnost a pravidla"
      actions={
        <SaveButton
          mutation={mutation}
          dirty={dirty && persona.trim().length > 0}
          onSave={() => mutation.mutate({ persona, rules: parsedRules })}
        />
      }
    >
      <div className="space-y-4">
        <Field label="Osobnost">
          <textarea
            value={persona}
            onChange={(e) => setPersona(e.target.value)}
            rows={4}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
          />
          <p className="mt-1 text-[11px] text-gray-500">
            Kdo agent je a jak mluví. Krátký odstavec stačí — čím delší text,
            tím méně se ho model drží.
          </p>
        </Field>
        <Field label="Pravidla">
          <textarea
            value={rules}
            onChange={(e) => setRules(e.target.value)}
            rows={8}
            className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm focus:border-[#1a2b6b] focus:outline-none"
          />
          <p className="mt-1 text-[11px] text-gray-500">
            Jedno pravidlo na řádek. Prázdné řádky se ignorují. Formulujte je
            jako pokyny, ne jako zákazy s výjimkami — model si výjimky vykládá
            po svém.
          </p>
        </Field>
      </div>
    </Section>
  );
};

export const PracticeAgentPage = () => {
  const practice = usePractice();
  if (!practice.agent) {
    return (
      <Section title="Agent V2">
        <p className="text-sm text-gray-600">
          Tato ordinace zatím nemá nastavení agenta V2. Přepněte ji na V2 na
          záložce Ordinace — výchozí nastavení se předvyplní a pak ho zde
          upravíte.
        </p>
      </Section>
    );
  }
  return (
    <>
      {practice.agentMode !== "realtime" && (
        <Section title="Agent V2">
          <p className="text-sm text-amber-800">
            Ordinace je momentálně na V1, takže se toto nastavení při hovorech
            nepoužije. Zůstává uložené a projeví se, jakmile ji přepnete zpět na
            V2.
          </p>
        </Section>
      )}
      <AgentModelSection practiceId={practice.id} agent={practice.agent} />
      <AgentPersonaSection practiceId={practice.id} agent={practice.agent} />
      <AgentPermissionsSection
        practiceId={practice.id}
        agent={practice.agent}
        requestTypes={practice.requestTypes}
      />
      <AgentToolsSection practiceId={practice.id} agent={practice.agent} />
      <PracticeAgentFaqSection
        practiceId={practice.id}
        agent={practice.agent}
      />
    </>
  );
};

// --- Blind transfer (V018) ---

/** Blind-transfer settings.
 *
 * Rendered for both generations. The number and the scheduled windows apply to either, so hiding them from a realtime
 * practice would leave its scheduled transfers unconfigurable; the press-9 controls are gated to scripted inside,
 * since V2 decides transfers through its own tool.
 *
 * Everything here reaches the in-call agent through the manifest, so the save chains renderPrompts. The Lambda
 * hash-checks each prompt, so nothing is re-synthesised; it is effectively a manifest refresh.
 */
const TransferSettingsSection = ({ practice }: { practice: PracticeFull }) => {
  const qc = useQueryClient();

  const initialNumber = practice.transferNumber ?? "";
  const initialUrgentNumber = practice.transferNumberUrgent ?? "";
  const initialB2bNumber = practice.transferNumberB2b ?? "";
  const initialAllSame = allNumbersSame({
    transferNumberUrgent: practice.transferNumberUrgent ?? null,
    transferNumberB2b: practice.transferNumberB2b ?? null,
  });
  const initialB2bEnabled = practice.transferB2bEnabled ?? false;
  const initialUrgentTypeId = practice.transferUrgentRequestTypeId ?? null;
  const initialB2bTypeId = practice.transferB2bRequestTypeId ?? null;
  const languages = practice.languages ?? [];
  const initialKeywords = useMemo(
    () =>
      Object.fromEntries(
        (practice.languages ?? []).map((l) => [
          l.language,
          l.b2bKeywords ?? [],
        ]),
      ),
    [practice.languages],
  );

  const [number, setNumber] = useState(initialNumber);
  const [allSame, setAllSame] = useState(initialAllSame);
  const [urgentNumber, setUrgentNumber] = useState(initialUrgentNumber);
  const [b2bNumber, setB2bNumber] = useState(initialB2bNumber);
  const [offerMode, setOfferMode] = useState(
    practice.transferOfferMode ?? "never",
  );
  const [confirmMode, setConfirmMode] = useState<TransferConfirmMode>(
    practice.transferConfirmMode ?? "none",
  );
  const [scheduleEnabled, setScheduleEnabled] = useState(
    practice.transferScheduleEnabled ?? false,
  );
  const [b2bEnabled, setB2bEnabled] = useState(initialB2bEnabled);
  const [urgentTypeId, setUrgentTypeId] = useState(initialUrgentTypeId);
  const [b2bTypeId, setB2bTypeId] = useState(initialB2bTypeId);
  const [keywordTexts, setKeywordTexts] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(initialKeywords).map(([language, keywords]) => [
        language,
        keywords.join("\n"),
      ]),
    ),
  );

  const canTransfer = practice.sipCarrier !== "odorik";
  // Press-1 is a V1 concept: V2 decides transfers through its own tool. Scheduled
  // windows apply to both, so the number and the schedule stay visible either way.
  const isScripted = (practice.agentMode ?? "scripted") !== "realtime";
  const transfersUrgentCalls =
    !isScripted && realtimeTransfersUrgentCalls(practice.agent?.transfer);

  const parsedKeywords = languages.map((l) => ({
    language: l.language,
    keywords: parseKeywordLines(keywordTexts[l.language] ?? ""),
  }));
  const keywordsDirty =
    isScripted &&
    b2bEnabled &&
    parsedKeywords.some(
      (k) =>
        JSON.stringify(k.keywords) !==
        JSON.stringify(initialKeywords[k.language] ?? []),
    );

  const validationErrors = isScripted
    ? transferRequestTypeErrors(
        {
          offerMode,
          b2bEnabled,
          urgentRequestTypeId: urgentTypeId,
          b2bRequestTypeId: b2bTypeId,
        },
        practice.requestTypes ?? [],
      )
    : realtimeTransferRequestTypeErrors(
        transfersUrgentCalls,
        urgentTypeId,
        practice.requestTypes ?? [],
      );

  const mutation = useMutation({
    mutationFn: async () => {
      const trimmed = number.trim();
      await api.updatePractice(practice.id, {
        displayName: practice.displayName,
        enabled: practice.enabled,
        voiceId: practice.voiceId,
        voiceSpeed: practice.voiceSpeed,
        transferNumber: trimmed.length > 0 ? trimmed : null,
        transferUrgentRequestTypeId: urgentTypeId,
        // Omitted for realtime so the stored V1 values survive a round trip
        // through V2 rather than being overwritten by controls that were hidden.
        ...(isScripted
          ? {
              transferOfferMode: offerMode,
              transferConfirmMode: confirmMode,
              transferB2bEnabled: b2bEnabled,
              transferB2bRequestTypeId: b2bTypeId,
              ...routeNumbersPatch(allSame, urgentNumber, b2bNumber),
            }
          : {}),
        transferScheduleEnabled: scheduleEnabled,
      });
      if (keywordsDirty) {
        await api.updateB2bKeywords(practice.id, parsedKeywords);
      }
      await api.renderPrompts(practice.id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["practice", practice.id] });
      pollPracticeUntilRenderedOrTimeout(qc, practice.id);
    },
  });

  const routeNumbersDirty =
    allSame !== initialAllSame ||
    (!allSame &&
      (urgentNumber.trim() !== initialUrgentNumber ||
        b2bNumber.trim() !== initialB2bNumber));

  const dirty =
    number.trim() !== initialNumber ||
    (isScripted && offerMode !== (practice.transferOfferMode ?? "never")) ||
    (isScripted && confirmMode !== (practice.transferConfirmMode ?? "none")) ||
    (isScripted && routeNumbersDirty) ||
    (isScripted && b2bEnabled !== initialB2bEnabled) ||
    urgentTypeId !== initialUrgentTypeId ||
    (isScripted && b2bTypeId !== initialB2bTypeId) ||
    keywordsDirty ||
    scheduleEnabled !== (practice.transferScheduleEnabled ?? false);

  const setKeywordText = (language: string, text: string) =>
    setKeywordTexts((prev) => ({ ...prev, [language]: text }));

  return (
    <Section
      title="Přepojení do ordinace"
      actions={
        <SaveButton
          mutation={mutation}
          dirty={dirty && validationErrors.length === 0}
        />
      }
    >
      {!canTransfer && (
        <p className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Operátor Odorik nepodporuje přepojení hovoru. Nastavení níže se uloží,
          ale neprojeví se, dokud ordinace nepřejde na IPEX nebo DIDWW (záložka
          Ordinace).
        </p>
      )}
      {validationErrors.length > 0 && (
        <div className="mb-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
          {validationErrors.map((e) => (
            <p key={e}>{e}</p>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Hlavní číslo (klasické přepojení)">
          <PhoneNumberInput value={number} onChange={setNumber} />
          <p className="mt-1 text-[11px] text-gray-500">
            Kanonický E.164. Použije se pro přepojení podle rozvrhu a pro agenta
            V2. Prázdné pole vypne přepojení úplně, bez ohledu na nastavení
            níže.
          </p>
        </Field>
        {isScripted && (
          <Field label="Čísla pro urgentní a B2B přepojení">
            <label className="mt-1 inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={allSame}
                onChange={(e) => setAllSame(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">
                Všechna čísla jsou stejná
              </span>
            </label>
            <p className="mt-1 text-[11px] text-gray-500">
              Zaškrtnuto = urgentní i B2B přepojení jde na hlavní číslo.
            </p>
          </Field>
        )}
        {isScripted && !allSame && (
          <Field label="Číslo pro urgentní přepojení (klávesa 9)">
            <PhoneNumberInput value={urgentNumber} onChange={setUrgentNumber} />
            <p className="mt-1 text-[11px] text-gray-500">
              Kanonický E.164. Prázdné pole = hlavní číslo.
            </p>
          </Field>
        )}
        {isScripted && !allSame && (
          <Field label="Číslo pro B2B přepojení">
            <PhoneNumberInput value={b2bNumber} onChange={setB2bNumber} />
            <p className="mt-1 text-[11px] text-gray-500">
              Kanonický E.164. Prázdné pole = hlavní číslo.
            </p>
          </Field>
        )}
        {isScripted && (
          <Field label="Kdy nabídnout „stiskněte 9“">
            <select
              value={offerMode}
              onChange={(e) => setOfferMode(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
            >
              <option value="never">Nikdy nenabízet</option>
              <option value="always">Nabídnout vždy</option>
              <option value="unknown_callers">Pouze u neznámých čísel</option>
            </select>
            <p className="mt-1 text-[11px] text-gray-500">
              Neznámé číslo = volající se neshoduje s žádným pacientem v Emmy.
              Nabídka zazní po úvodní hlášce, ale devítku lze stisknout kdykoli
              během hovoru.
            </p>
          </Field>
        )}
        {isScripted && (
          <Field label="Potvrzení před přepojením">
            <select
              value={confirmMode}
              onChange={(e) =>
                setConfirmMode(e.target.value === "simple" ? "simple" : "none")
              }
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
            >
              <option value="none">Bez potvrzení</option>
              <option value="simple">Jednoduché potvrzení</option>
            </select>
            <p className="mt-1 text-[11px] text-gray-500">
              Jednoduché potvrzení přehraje varování, že přepojení je jen pro
              vážné ohrožení života nebo zdraví, a vyžádá si „ano“. Odmítnutí
              vrátí volajícího zpět tam, kde přestal.
            </p>
          </Field>
        )}
        {(!isScripted || offerMode !== "never") && (
          <Field label="Typ požadavku pro urgentní přepojení">
            <RequestTypeSelect
              value={urgentTypeId}
              onChange={setUrgentTypeId}
              requestTypes={practice.requestTypes ?? []}
            />
            {!isScripted && (
              <p className="mt-1 text-[11px] text-gray-500">
                Pod tímto typem se do Emmy zapíše každé urgentní přepojení,
                povedené i nepovedené.
              </p>
            )}
          </Field>
        )}
        {isScripted && (
          <Field label="B2B dotaz pro neznámá čísla">
            <label className="mt-1 inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={b2bEnabled}
                onChange={(e) => setB2bEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">
                Zeptat se, zda volající volá jako pacient
              </span>
            </label>
            <p className="mt-1 text-[11px] text-gray-500">
              Po úvodní hlášce se volajícího, jehož číslo nepatří žádnému
              známému pacientovi, zeptáme, zda volá jako pacient. Odpověď „ne“
              nebo odpověď s některým z klíčových slov ho přepojí na číslo pro
              B2B přepojení. Známí pacienti dotaz neuslyší.
            </p>
          </Field>
        )}
        {isScripted && b2bEnabled && (
          <Field label="Typ požadavku pro B2B">
            <RequestTypeSelect
              value={b2bTypeId}
              onChange={setB2bTypeId}
              requestTypes={practice.requestTypes ?? []}
            />
          </Field>
        )}
        <Field label="Přepojovat podle rozvrhu">
          <label className="mt-1 inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={scheduleEnabled}
              onChange={(e) => setScheduleEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">
              Ve zvolených oknech přepojit každý hovor
            </span>
          </label>
          <p className="mt-1 text-[11px] text-gray-500">
            Okna platí jen uvnitř ordinačních hodin. Mimo ně a během dovolené se
            nepřepojuje. Vypnutí zachová rozvrh, jen se nepoužije.
          </p>
        </Field>
      </div>
      {isScripted && b2bEnabled && (
        <B2bKeywordsEditor
          languages={languages}
          texts={keywordTexts}
          onChange={setKeywordText}
        />
      )}
    </Section>
  );
};

const PhoneNumberInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => (
  <input
    type="tel"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder="e.g. +420603717155"
    className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm placeholder:text-gray-300 focus:border-[#1a2b6b] focus:outline-none"
    autoComplete="off"
    spellCheck={false}
  />
);

const RequestTypeSelect = ({
  value,
  onChange,
  requestTypes,
}: {
  value: string | null;
  onChange: (typeId: string | null) => void;
  requestTypes: RequestType[];
}) => {
  const sorted = useMemo(
    () => [...requestTypes].sort((a, b) => a.position - b.position),
    [requestTypes],
  );
  const known = sorted.some((t) => t.typeId === value);
  return (
    <select
      value={known && value !== null ? value : ""}
      onChange={(e) =>
        onChange(e.target.value.length > 0 ? e.target.value : null)
      }
      className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
    >
      <option value="">— vyberte —</option>
      {sorted.map((t) => (
        <option key={t.typeId} value={t.typeId}>
          {t.displayName}
        </option>
      ))}
    </select>
  );
};

const B2bKeywordsEditor = ({
  languages,
  texts,
  onChange,
}: {
  languages: PracticeLanguage[];
  texts: Record<string, string>;
  onChange: (language: string, text: string) => void;
}) => (
  <div className="mt-4">
    <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600">
      Klíčová slova B2B
    </span>
    <p className="mb-2 text-[11px] text-gray-500">
      Jedno slovo nebo spojení na řádek. Stačí začátek slova, např. „pojišťovn“
      zachytí pojišťovna i pojišťovny. Velikost písmen a diakritika nehrají
      roli.
    </p>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {languages.map((l) => (
        <Field key={l.language} label={languageLabel(l)}>
          <textarea
            value={texts[l.language] ?? ""}
            onChange={(e) => onChange(l.language, e.target.value)}
            rows={6}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
          />
        </Field>
      ))}
    </div>
  </div>
);

const languageLabel = (l: PracticeLanguage): string => {
  const name =
    OFFERABLE_LANGUAGES.find((o) => o.code === l.language)?.label ??
    l.language.toUpperCase();
  return l.isMain ? `${name} (hlavní)` : name;
};

/** Weekly transfer windows. Same grid as the opening-hours editor on purpose: an admin who has filled one in already
 *  knows how this one behaves, and the backend contract is identical (replace-all, three blocks a day). */
const TransferWindowsSection = ({
  practiceId,
  initial,
}: {
  practiceId: string;
  initial: TransferWindow[];
}) => {
  const qc = useQueryClient();

  const initialGrid = useMemo(
    () =>
      toGrid(
        initial.map((w) => ({
          weekday: w.weekday,
          blockIndex: w.blockIndex,
          openTime: w.startTime,
          closeTime: w.endTime,
        })),
      ),
    [initial],
  );
  const [grid, setGrid] = useState<GridCell[][]>(initialGrid);
  useEffect(() => setGrid(initialGrid), [initialGrid]);

  const mutation = useMutation({
    mutationFn: async () => {
      const windows = fromGrid(grid).map((h) => ({
        weekday: h.weekday,
        blockIndex: h.blockIndex,
        startTime: h.openTime,
        endTime: h.closeTime,
      }));
      await api.updateTransferWindows(practiceId, windows);
      await api.renderPrompts(practiceId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["practice", practiceId] });
      pollPracticeUntilRenderedOrTimeout(qc, practiceId);
    },
  });

  const dirty = JSON.stringify(grid) !== JSON.stringify(initialGrid);

  return (
    <Section
      title="Okna pro automatické přepojení"
      actions={
        <SaveButton
          mutation={mutation}
          dirty={dirty}
          blocked={hasGridErrors(grid)}
        />
      }
    >
      <p className="mb-3 text-xs text-gray-500">
        V těchto oknech se každý příchozí hovor po krátké hlášce přepojí do
        ordinace. Okna se uplatní pouze uvnitř ordinačních hodin. Čas pište
        číslem, např. 7, 730 nebo 7:30. Prázdné okno = neaktivní.
      </p>
      <WeeklyBlocksEditor grid={grid} onChange={setGrid} blockLabel="Okno" />
    </Section>
  );
};

// --- Vacation blocks ---

const pragueOffset = (localIso: string): string => {
  const asIfUtc = new Date(`${localIso}:00Z`);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Prague",
    timeZoneName: "longOffset",
  });
  const parts = fmt.formatToParts(asIfUtc);
  const offPart =
    parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT+01:00";
  const m = offPart.match(/([+-]\d{2}:\d{2})/);
  return m ? m[1] : "+01:00";
};

const toPragueIso = (localIso: string): string =>
  `${localIso}:00${pragueOffset(localIso)}`;

/** UTC ISO ↔ Prague wall-clock: `"2026-04-22T00:01:00+02:00"` → `"2026-04-22T00:01"` (the format a
 *  `<input type="datetime-local">` expects). Inverse of `toPragueIso` for display-in-picker.
 */
const toPragueLocal = (iso: string): string => {
  const d = new Date(iso);
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Prague",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(d);
  const g = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return `${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}`;
};

const humanDuration = (startsAt: string, endsAt: string): string => {
  const diff = new Date(endsAt).getTime() - new Date(startsAt).getTime();
  if (diff <= 0) return "—";
  const days = Math.floor(diff / (24 * 3600 * 1000));
  const hours = Math.floor((diff % (24 * 3600 * 1000)) / (3600 * 1000));
  if (days > 0 && hours > 0) return `${days} d ${hours} h`;
  if (days > 0) return `${days} d`;
  return `${hours} h`;
};

const VacationBlocksSection = ({
  practiceId,
  initial,
  credsConfigured,
}: {
  practiceId: string;
  initial: VacationBlock[];
  credsConfigured: boolean;
}) => {
  const qc = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  const [newText, setNewText] = useState("");
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const addMutation = useMutation({
    // Chain the render right after the insert — the admin already typed the text, so there's nothing more for them
    // to confirm. Hash-check in the Lambda keeps this cheap (only the new block actually synthesizes).
    mutationFn: async () => {
      await api.addVacationBlock(
        practiceId,
        toPragueIso(startLocal),
        toPragueIso(endLocal),
        newText,
      );
      await api.renderPrompts(practiceId);
    },
    onSuccess: () => {
      setStartLocal("");
      setEndLocal("");
      setNewText("");
      setShowAddForm(false);
      pollPracticeUntilRenderedOrTimeout(qc, practiceId);
    },
  });

  // Deleting a block removes it from the manifest only after a re-render — otherwise the agent keeps gating calls
  // on the deleted closure until someone else clicks re-render. Hash-check keeps the render call cheap.
  const deleteMutation = useMutation({
    mutationFn: async (blockId: string) => {
      await api.deleteVacationBlock(practiceId, blockId);
      await api.renderPrompts(practiceId);
    },
    onSuccess: () => pollPracticeUntilRenderedOrTimeout(qc, practiceId),
  });

  // Sync pulls closures from Emmy, upserts them as admin-invisible source='emmy' blocks, and chains a render so the
  // manifest picks up any new or removed closures. Emmy blocks have no Czech text so the render is metadata-only.
  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await api.syncEmmyClosures(practiceId);
      await api.renderPrompts(practiceId);
      return res;
    },
    onSuccess: (res) => {
      setSyncMessage(
        `Synchronizováno: ${res.newlyAdded.length} přidáno, ${res.unchanged} již existovalo`,
      );
      pollPracticeUntilRenderedOrTimeout(qc, practiceId);
    },
  });

  const canSubmitAdd =
    startLocal.length > 0 &&
    endLocal.length > 0 &&
    new Date(startLocal) < new Date(endLocal) &&
    newText.trim().length > 0;

  // Sync is admin-only (PRD-010 Stage 2 — "NOT Emmy wiring"). Hide the button for DoctorUsers.
  const isDoctor = useIsDoctor();

  return (
    <Section
      title="Dovolená"
      actions={
        !isDoctor && (
          <button
            onClick={() => syncMutation.mutate()}
            disabled={!credsConfigured || syncMutation.isPending}
            title={
              !credsConfigured
                ? "Nejprve propojte Emmy výše"
                : "Stáhnout dovolené z Emmy"
            }
            className="rounded border border-[#1a2b6b] px-3 py-1.5 text-xs text-[#1a2b6b] hover:bg-[#1a2b6b] hover:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent"
          >
            {syncMutation.isPending
              ? "Synchronizuji…"
              : "Synchronizovat z Emmy"}
          </button>
        )
      }
    >
      <p className="mb-3 text-xs text-gray-500">
        Každý blok má svou vlastní zprávu, kterou Emmy přehraje volajícím v
        daném období. Po úpravě textu klikněte na <em>Uložit a přegenerovat</em>
        , jinak se změna v hovoru neprojeví.
      </p>

      {syncMessage && (
        <div
          aria-live="polite"
          className="mb-3 rounded border border-green-300 bg-green-50 p-2 text-xs text-green-800"
        >
          {syncMessage}
        </div>
      )}
      {syncMutation.isError && (
        <div className="mb-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
          Chyba synchronizace: {(syncMutation.error as Error).message}
        </div>
      )}

      {initial.length === 0 ? (
        <p className="text-sm text-gray-500">Žádná dovolená.</p>
      ) : (
        <div className="space-y-3">
          {initial.map((b) => (
            <VacationBlockRow
              key={b.id}
              practiceId={practiceId}
              block={b}
              onDelete={(id) => deleteMutation.mutate(id)}
            />
          ))}
        </div>
      )}
      {deleteMutation.isError && (
        <p className="mt-2 text-xs text-red-600">
          {(deleteMutation.error as Error).message}
        </p>
      )}

      {showAddForm ? (
        <div className="mt-4 rounded border border-gray-200 p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Od (Evropa/Praha)">
              <input
                type="datetime-local"
                value={startLocal}
                onChange={(e) => setStartLocal(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Do (Evropa/Praha)">
              <input
                type="datetime-local"
                value={endLocal}
                onChange={(e) => setEndLocal(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Text zprávy pro tento blok">
              <textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                rows={3}
                placeholder="např. Dobrý den, ordinace je do 15. července na letní dovolené. Volejte prosím po tomto datu."
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
              />
            </Field>
          </div>
          {addMutation.isError && (
            <p className="mt-2 text-xs text-red-600">
              {(addMutation.error as Error).message}
            </p>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={() => {
                setShowAddForm(false);
                setStartLocal("");
                setEndLocal("");
                setNewText("");
              }}
              className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100"
            >
              Zrušit
            </button>
            <button
              onClick={() => addMutation.mutate()}
              disabled={!canSubmitAdd || addMutation.isPending}
              title={!canSubmitAdd ? "Vyplňte datumy a text zprávy" : undefined}
              className="rounded bg-[#1a2b6b] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0f1d52] disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {addMutation.isPending ? "Přidávám…" : "Přidat blok"}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="mt-4 rounded border border-[#1a2b6b] px-3 py-1.5 text-xs text-[#1a2b6b] hover:bg-[#1a2b6b] hover:text-white"
        >
          + Přidat blok
        </button>
      )}
    </Section>
  );
};

/** One vacation block row: editable dates + text + per-row "Uložit a přegenerovat" button.
 *  Emmy-sourced blocks are fully read-only — they must be edited in Emmy itself, then synced back. Saving sends a
 *  partial patch of just the changed fields to the PUT endpoint and chains renderPrompts — the render Lambda's
 *  hash-check ensures only the changed block's audio is re-synthesised (and dates-only changes skip TTS entirely). */
const VacationBlockRow = ({
  practiceId,
  block,
  onDelete,
}: {
  practiceId: string;
  block: VacationBlock;
  onDelete: (id: string) => void;
}) => {
  const qc = useQueryClient();
  const initialStart = toPragueLocal(block.startsAt);
  const initialEnd = toPragueLocal(block.endsAt);
  const [text, setText] = useState(block.czechText);
  const [startLocal, setStartLocal] = useState(initialStart);
  const [endLocal, setEndLocal] = useState(initialEnd);
  useEffect(() => setText(block.czechText), [block.czechText]);
  useEffect(() => setStartLocal(initialStart), [initialStart]);
  useEffect(() => setEndLocal(initialEnd), [initialEnd]);

  const textDirty = text !== block.czechText;
  const startDirty = startLocal !== initialStart;
  const endDirty = endLocal !== initialEnd;
  const dirty = textDirty || startDirty || endDirty;
  const isEmmy = block.source === "emmy";
  const datesValid =
    startLocal.length > 0 &&
    endLocal.length > 0 &&
    new Date(startLocal) < new Date(endLocal);

  const saveAndRender = useMutation({
    mutationFn: async () => {
      const patch: { startsAt?: string; endsAt?: string; czechText?: string } =
        {};
      if (textDirty) patch.czechText = text;
      // PUT enforces "dates together or not at all", so if either picker changed we send the pair.
      if (startDirty || endDirty) {
        patch.startsAt = toPragueIso(startLocal);
        patch.endsAt = toPragueIso(endLocal);
      }
      await api.updateVacationBlock(practiceId, block.id, patch);
      await api.renderPrompts(practiceId);
    },
    // render-prompts is fire-and-forget; poll the practice query for a while so the UI catches the new renderedAt.
    onSuccess: () => pollPracticeUntilRenderedOrTimeout(qc, practiceId),
  });

  return (
    <div className="rounded border border-gray-200 p-3">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="text-xs text-gray-500">
          {humanDuration(toPragueIso(startLocal), toPragueIso(endLocal))}
        </div>
        <div className="flex items-center gap-2">
          {isEmmy ? (
            <span
              className="rounded bg-blue-100 px-2 py-0.5 text-[10px] uppercase tracking-wider text-blue-800"
              title="Blok pochází z Emmy — upravte ho v Emmy a synchronizujte znovu"
            >
              Emmy
            </span>
          ) : (
            <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] uppercase tracking-wider text-gray-600">
              Ručně
            </span>
          )}
          <button
            onClick={() => {
              if (window.confirm("Smazat tento blok?")) onDelete(block.id);
            }}
            disabled={isEmmy}
            title={
              isEmmy
                ? "Emmy blok nelze smazat zde — upravte v Emmy"
                : "Smazat blok"
            }
            className="text-xs text-red-600 hover:underline disabled:cursor-not-allowed disabled:text-gray-300 disabled:no-underline"
          >
            ✕ Smazat
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Od (Evropa/Praha)">
          <input
            type="datetime-local"
            value={startLocal}
            onChange={(e) => setStartLocal(e.target.value)}
            disabled={isEmmy}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
          />
        </Field>
        <Field label="Do (Evropa/Praha)">
          <input
            type="datetime-local"
            value={endLocal}
            onChange={(e) => setEndLocal(e.target.value)}
            disabled={isEmmy}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
          />
        </Field>
      </div>

      <div className="mt-3">
        <Field label="Text zprávy pro tento blok">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            disabled={isEmmy}
            placeholder={
              isEmmy
                ? "Emmy bloky nemají vlastní text. Smažte blok a vytvořte ručně, pokud chcete vlastní zprávu."
                : "např. Dobrý den, ordinace je do 15. července na letní dovolené."
            }
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none disabled:bg-gray-50 disabled:text-gray-500"
          />
        </Field>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">
            {saveAndRender.isPending
              ? "— přegeneruji…"
              : block.renderedAt
                ? `vygenerováno ${new Date(block.renderedAt).toLocaleString("cs-CZ")}`
                : "— zatím nevygenerováno"}
          </span>
          {!saveAndRender.isPending && (
            <VacationBlockListenButton practiceId={practiceId} block={block} />
          )}
        </div>
        <div className="flex items-center gap-2">
          {saveAndRender.isError && (
            <span className="text-xs text-red-600">
              {(saveAndRender.error as Error).message}
            </span>
          )}
          <button
            onClick={() => saveAndRender.mutate()}
            disabled={
              isEmmy ||
              !dirty ||
              saveAndRender.isPending ||
              text.trim().length === 0 ||
              !datesValid
            }
            title={
              isEmmy
                ? "Emmy bloky nelze upravit"
                : text.trim().length === 0
                  ? "Text zprávy nesmí být prázdný"
                  : !datesValid
                    ? "Začátek musí být před koncem"
                    : !dirty
                      ? "Žádné neuložené změny"
                      : "Uložit a přegenerovat"
            }
            className="rounded bg-[#1a2b6b] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0f1d52] disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {saveAndRender.isPending ? "Ukládám…" : "Uložit a přegenerovat"}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Lazy listen button for a vacation-block's rendered audio. Follows the same pattern as `PromptListenButton`:
 *  disabled until the block has been rendered, fetches a presigned S3 URL on first click, then inline-plays. */
const VacationBlockListenButton = ({
  practiceId,
  block,
}: {
  practiceId: string;
  block: VacationBlock;
}) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => setUrl(null), [block.renderedAt]);

  const mutation = useMutation({
    mutationFn: () => api.getVacationBlockAudioUrl(practiceId, block.id),
    onSuccess: (r) => setUrl(r.url),
  });

  if (!block.renderedAt) return null;
  if (url) return <audio controls src={url} className="h-8" />;

  return (
    <button
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="rounded border border-[#1a2b6b] px-3 py-1 text-xs text-[#1a2b6b] hover:bg-[#1a2b6b] hover:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent"
    >
      {mutation.isPending ? "Načítám…" : "▶ Přehrát"}
    </button>
  );
};

// --- Shared ---

const Section = ({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) => (
  <section className="rounded border border-gray-200 bg-white p-6">
    <div className="mb-4 flex items-center justify-between">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
        {title}
      </h3>
      {actions}
    </div>
    {children}
  </section>
);

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600">
      {label}
    </span>
    {children}
  </label>
);

type MutationLike = {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  /** Widened from `() => void` so a section whose mutation takes an argument can still be passed here. Such sections
   *  supply `onSave` and SaveButton never calls this itself; the looser signature only has to accept both shapes. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutate: (...args: any[]) => void;
};

const SaveButton = ({
  mutation,
  dirty,
  onSave,
  blocked = false,
}: {
  mutation: MutationLike;
  dirty: boolean;
  /** Supply when the mutation needs an argument. The agent sections use it to send only the keys they own, so one
   *  section cannot overwrite another's fields. Without it the mutation is called with no argument, which is what
   *  every section that closes over its own state does. */
  onSave?: () => void;
  /** A field holds a value the section cannot save; the section says which. */
  blocked?: boolean;
}) => (
  <div className="flex items-center gap-3">
    {mutation.isError && (
      <span className="text-xs text-red-600">
        {(mutation.error as Error).message}
      </span>
    )}
    <button
      onClick={() => (onSave ? onSave() : mutation.mutate())}
      disabled={!dirty || blocked || mutation.isPending}
      className="rounded bg-[#1a2b6b] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0f1d52] disabled:cursor-not-allowed disabled:bg-gray-300"
    >
      {mutation.isPending ? "Ukládám…" : dirty ? "Uložit" : "Uloženo"}
    </button>
  </div>
);

const ConfirmModal = ({
  title,
  message,
  onConfirm,
  onClose,
}: {
  title: string;
  message: string;
  onConfirm: () => void;
  onClose: () => void;
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="w-full max-w-md rounded border border-gray-200 bg-white p-6 shadow-lg">
      <h4 className="mb-3 text-sm font-semibold text-[#1a2b6b]">{title}</h4>
      <p className="mb-4 text-sm text-gray-700">{message}</p>
      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100"
        >
          Zrušit
        </button>
        <button
          onClick={onConfirm}
          className="rounded bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
        >
          Smazat
        </button>
      </div>
    </div>
  </div>
);

// --- request-types helpers ---

const withPositions = (types: RequestType[]): RequestType[] =>
  types.map((t, i) => ({ ...t, position: i }));

const moveJineLast = (types: RequestType[]): RequestType[] => {
  const nonJine = types.filter((t) => t.typeId !== JINE_TYPE_ID);
  const jine = types.find((t) => t.typeId === JINE_TYPE_ID);
  return jine ? [...nonJine, jine] : nonJine;
};

const clamp = (n: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, n));

/** Re-invalidate the practice query every 2.5s for up to 30s so the UI catches up with the async render-prompts
 *  Lambda writing `rendered_at` back to the DB. The 3-point setTimeout approach we had before was too sparse — long
 *  TTS calls (e.g. long vacation messages) could finish between polls and leave the UI showing stale state until a
 *  manual refresh.
 */
const pollPracticeUntilRenderedOrTimeout = (
  qc: QueryClient,
  practiceId: string,
): void => {
  void qc.invalidateQueries({ queryKey: ["practice", practiceId] });
  const start = Date.now();
  const interval = setInterval(() => {
    void qc.invalidateQueries({ queryKey: ["practice", practiceId] });
    if (Date.now() - start > 30_000) clearInterval(interval);
  }, 2500);
};
